import { formatarUnitario, totalLinha } from '../utils/arredondamentoUnitario';
import LiquidText from '../components/LiquidText';
import ProcedimentoLink from '../components/ProcedimentoLink';
import { OrcamentoDialog, OrcamentoIcon, OrcamentoFeedbackProvider, useOrcamentoFeedback } from '../components/OrcamentoUI';
import { useMaquetesTheme } from '../context/MaquetesThemeContext';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import {
    BarChart, Bar, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { fmtMoney as fmt } from '../utils/moeda';
import './Orcamentos.css';

/* ============================================================
   CACHE EM MEMÓRIA (persiste entre navegações na mesma sessão)
   ============================================================ */
const _cache = {
    stats: null,
    anos: null,
    // orcamentos por chave "ano|estado|search|dataInicio|dataFim"
    listas: {},
};
function listaCacheKey(ano, estado, search, dataInicio, dataFim) {
    return `${ano}|${estado}|${search}|${dataInicio}|${dataFim}`;
}
export function invalidateCache() {
    _cache.stats = null;
    _cache.anos = null;
    _cache.listas = {};
}

/* ============================================================
   CONSTANTES
   ============================================================ */
const ESTADOS = [
    { value: '', label: 'Todos' },
    { value: 'EM_ORCAMENTO', label: 'Em Orçamento' },
    { value: 'EM_PRODUCAO',  label: 'Em Produção' },
    { value: 'ENVIADO',      label: 'Enviado' },
    { value: 'PARA_FATURAR', label: 'Para Faturar' },
    { value: 'CONCLUIDO',    label: 'Concluído' },
    { value: 'FATURADO',     label: 'Faturado' },
    { value: 'CANCELADO',    label: 'Cancelado' },
    { value: 'PENDENTE',     label: 'Pendente' },
];
const ESTADO_LABELS = {
    EM_ORCAMENTO: 'EM ORÇAMENTO', EM_PRODUCAO: 'EM PRODUÇÃO',
    ENVIADO: 'ENVIADO', PARA_FATURAR: 'PARA FATURAR', CONCLUIDO: 'CONCLUÍDO',
    FATURADO: 'FATURADO', FACTURADO: 'FATURADO', CANCELADO: 'CANCELADO', PENDENTE: 'PENDENTE',
};
const ESTADO_CLASSES = {
    EM_ORCAMENTO: 'estado-orcamento', EM_PRODUCAO: 'estado-producao',
    ENVIADO: 'estado-enviado', PARA_FATURAR: 'estado-faturar', CONCLUIDO: 'estado-concluido',
    FATURADO: 'estado-facturado', FACTURADO: 'estado-facturado', CANCELADO: 'estado-cancelado', PENDENTE: 'estado-pendente',
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '';

/* ============================================================
   MODAL CLIENTE
   ============================================================ */
const CONDICOES_PAGAMENTO = ['30 dias', '60 dias', '90 dias', 'Pronto pagamento', 'Contra entrega'];

function emptyContacto() { return { contacto: '', funcao: '', telefone: '', telemovel: '', email: '' }; }

function ModalCliente({ cliente, onClose, onSaved }) {
    const isEdit = !!cliente?.id;

    const [form, setForm] = useState(isEdit ? {
        nome: cliente.nome || '',
        nif: cliente.nif || '',
        morada: cliente.morada || '',
        codigo_postal: cliente.codigo_postal || '',
        localidade: cliente.localidade || '',
        pais: cliente.pais || '',
        telefone: cliente.telefone || '',
        telemovel: cliente.telemovel || '',
        email: cliente.email || '',
        condicao_pagamento: cliente.condicao_pagamento || '',
    } : {
        nome: '', nif: '', morada: '', codigo_postal: '', localidade: '',
        pais: '', telefone: '', telemovel: '', email: '', condicao_pagamento: ''
    });

    const [contactos, setContactos] = useState(() => {
        if (isEdit && Array.isArray(cliente.contactos) && cliente.contactos.length > 0) {
            const ct = cliente.contactos.slice(0, 3).map(c => ({
                contacto: c.contacto || '', funcao: c.funcao || '',
                telefone: c.telefone || '', telemovel: c.telemovel || '', email: c.email || ''
            }));
            while (ct.length < 3) ct.push(emptyContacto());
            return ct;
        }
        return [emptyContacto(), emptyContacto(), emptyContacto()];
    });

    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const setC = (idx, field, value) => {
        setContactos(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
    };

    const handleSave = async () => {
        if (!form.nome) { setErr('Nome é obrigatório'); return; }
        setSaving(true); setErr('');
        try {
            const payload = { ...form, contactos: contactos.filter(c => c.contacto) };
            if (isEdit) await api.put(`/clientes/${cliente.id}`, payload);
            else await api.post('/clientes', payload);
            onSaved();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar');
        } finally { setSaving(false); }
    };

    const f = (field, label, type = 'text', full = false) => (
        <div className={`modal-field ${full ? 'full' : ''}`}>
            <label><LiquidText>{label}</LiquidText></label>
            <input type={type} value={form[field] || ''}
                onChange={e => setForm({ ...form, [field]: e.target.value })} />
        </div>
    );

    return (
        <OrcamentoDialog onClose={onClose}>
            <div className="modal-form modal-form-large" onClick={e => e.stopPropagation()}>
                <div className="modal-form-header">
                    <h3>{isEdit ? `Editar Cliente #${cliente.id}` : 'Novo Cliente'}</h3>
                    <button className="modal-close" aria-label="Fechar" onClick={onClose}><OrcamentoIcon name="close" fallback="✕" /></button>
                </div>

                <div className="modal-form-body">
                <div className="modal-form-grid">
                    {f('nome', 'CLIENTE *', 'text', true)}
                    {f('nif', 'NIF')}
                    {f('morada', 'MORADA', 'text', true)}
                    {f('codigo_postal', 'CÓDIGO POSTAL')}
                    {f('localidade', 'LOCALIDADE')}
                    {f('pais', 'PAÍS')}
                    {f('telefone', 'TELEFONE')}
                    {f('telemovel', 'TELEMÓVEL')}
                    {f('email', 'E-MAIL', 'email', true)}
                    <div className="modal-field">
                        <label>CONDIÇÃO PAGAMENTO</label>
                        <select value={form.condicao_pagamento || ''}
                            onChange={e => setForm({ ...form, condicao_pagamento: e.target.value })}>
                            <option value=""></option>
                            {CONDICOES_PAGAMENTO.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>

                {[0, 1, 2].map(idx => (
                    <div key={idx} className="modal-contacto-section">
                        <div className="modal-contacto-title">Contacto {idx + 1}</div>
                        <div className="modal-form-grid">
                            <div className="modal-field full">
                                <label>CONTACTO</label>
                                <input value={contactos[idx].contacto} onChange={e => setC(idx, 'contacto', e.target.value)} />
                            </div>
                            <div className="modal-field full">
                                <label>FUNÇÃO</label>
                                <input value={contactos[idx].funcao} onChange={e => setC(idx, 'funcao', e.target.value)} />
                            </div>
                            <div className="modal-field">
                                <label>TELEFONE</label>
                                <input value={contactos[idx].telefone} onChange={e => setC(idx, 'telefone', e.target.value)} />
                            </div>
                            <div className="modal-field">
                                <label>TELEMÓVEL</label>
                                <input value={contactos[idx].telemovel} onChange={e => setC(idx, 'telemovel', e.target.value)} />
                            </div>
                            <div className="modal-field full">
                                <label>E-MAIL</label>
                                <input type="email" value={contactos[idx].email} onChange={e => setC(idx, 'email', e.target.value)} />
                            </div>
                        </div>
                    </div>
                ))}

                {err && <div className="modal-err">{err}</div>}
                <div className="modal-form-actions">
                    <button className="btn-modal-cancel" onClick={onClose}>Cancelar</button>
                    <button className="btn-modal-save" onClick={handleSave} disabled={saving}>
                        {saving ? 'A guardar...' : 'Guardar'}
                    </button>
                </div>
                </div>{/* /modal-form-body */}
            </div>
        </OrcamentoDialog>
    );
}

/* ============================================================
   MODAL FORNECEDOR
   ============================================================ */
const CONDICOES_PAGAMENTO_FORN = [
    '', '15 dias', '30 dias', '45 dias', '60 dias', '90 dias', 'Pronto pagamento', 'Débito direto'
];

const EMPTY_CONTACTO_FORN = { contacto: '', funcao: '', telefone: '', telemovel: '', email: '' };

function ModalFornecedor({ fornecedor, onClose, onSaved }) {
    const [form, setForm] = useState(fornecedor ? {
        nome: fornecedor.nome || '',
        abreviatura: fornecedor.abreviatura || '',
        nif: fornecedor.nif || '',
        email: fornecedor.email || '',
        telefone: fornecedor.telefone || '',
        telemovel: fornecedor.telemovel || '',
        morada: fornecedor.morada || '',
        codigo_postal: fornecedor.codigo_postal || '',
        localidade: fornecedor.localidade || '',
        pais: fornecedor.pais || '',
        condicao_pagamento: fornecedor.condicao_pagamento || '',
        notas: fornecedor.notas || '',
        critico_sistema: fornecedor.critico_sistema === true,
    } : {
        nome: '', abreviatura: '', nif: '', email: '', telefone: '', telemovel: '',
        morada: '', codigo_postal: '', localidade: '', pais: '',
        condicao_pagamento: '', notas: '', critico_sistema: false,
    });
    const [contactos, setContactos] = useState(() => {
        const base = [{ ...EMPTY_CONTACTO_FORN }, { ...EMPTY_CONTACTO_FORN }, { ...EMPTY_CONTACTO_FORN }];
        if (fornecedor?.contactos) {
            fornecedor.contactos.forEach((c, i) => { if (i < 3) base[i] = { ...EMPTY_CONTACTO_FORN, ...c }; });
        }
        return base;
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const isEdit = !!fornecedor?.id;

    const handleSave = async () => {
        if (!form.nome) { setErr('Nome é obrigatório'); return; }
        setSaving(true); setErr('');
        try {
            const payload = { ...form, contactos: contactos.filter(c => c.contacto.trim()) };
            if (isEdit) await api.put(`/fornecedores/${fornecedor.id}`, payload);
            else await api.post('/fornecedores', payload);
            onSaved();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar');
        } finally { setSaving(false); }
    };

    const f = (field, label, type = 'text', full = false) => (
        <div className={`modal-field${full ? ' full' : ''}`}>
            <label><LiquidText>{label}</LiquidText></label>
            <input type={type} value={form[field] || ''}
                onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} />
        </div>
    );

    const fc = (idx, field, label) => (
        <div className="modal-field">
            <label><LiquidText>{label}</LiquidText></label>
            <input type="text" value={contactos[idx][field] || ''}
                onChange={e => setContactos(prev => {
                    const arr = [...prev];
                    arr[idx] = { ...arr[idx], [field]: e.target.value };
                    return arr;
                })} />
        </div>
    );

    return (
        <OrcamentoDialog onClose={onClose}>
            <div className="modal-form modal-form-large" onClick={e => e.stopPropagation()}>
                <div className="modal-form-header">
                    <h3>{isEdit ? 'Editar Fornecedor' : 'Novo Fornecedor'}</h3>
                    <button className="modal-close" aria-label="Fechar" onClick={onClose}><OrcamentoIcon name="close" fallback="✕" /></button>
                </div>
                <div className="modal-form-body">
                <div className="modal-form-grid">
                    {f('nome', 'Nome *', 'text', true)}
                    {/* Nome curto para avisos e listagens onde a designação
                        social não cabe. Deixar vazio repõe a sugestão. */}
                    {f('abreviatura', 'Abreviatura')}
                    {f('nif', 'NIF')}
                    {f('telefone', 'Telefone')}
                    {f('telemovel', 'Telemóvel')}
                    {f('email', 'E-Mail', 'email', true)}
                    {f('morada', 'Morada', 'text', true)}
                    {f('codigo_postal', 'Código Postal')}
                    {f('localidade', 'Localidade')}
                    {f('pais', 'País')}
                    <div className="modal-field">
                        <label>Condição Pagamento</label>
                        <select value={form.condicao_pagamento || ''}
                            onChange={e => setForm(p => ({ ...p, condicao_pagamento: e.target.value }))}>
                            {CONDICOES_PAGAMENTO_FORN.map(o => <option key={o} value={o}>{o || '— Selecionar —'}</option>)}
                        </select>
                    </div>
                    <div className="modal-field full">
                        <label>Notas</label>
                        <textarea value={form.notas || ''} onChange={e => setForm(p => ({ ...p, notas: e.target.value }))} rows={2} />
                    </div>
                    <div className="modal-field full">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" style={{ width: 'auto' }}
                                checked={form.critico_sistema === true}
                                onChange={e => setForm(p => ({ ...p, critico_sistema: e.target.checked }))} />
                            Fornecedor crítico para o sistema (avaliado sempre, SGI)
                        </label>
                    </div>
                </div>

                {[0, 1, 2].map(idx => (
                    <div key={idx} className="modal-contacto-section">
                        <div className="modal-contacto-title">Contacto {idx + 1}</div>
                        <div className="modal-form-grid">
                            {fc(idx, 'contacto', 'Nome')}
                            {fc(idx, 'funcao', 'Função')}
                            {fc(idx, 'telefone', 'Telefone')}
                            {fc(idx, 'telemovel', 'Telemóvel')}
                            {fc(idx, 'email', 'E-Mail')}
                        </div>
                    </div>
                ))}

                {err && <div className="modal-err">{err}</div>}
                </div>
                <div className="modal-form-actions">
                    <button className="btn-modal-cancel" onClick={onClose}>Cancelar</button>
                    <button className="btn-modal-save" onClick={handleSave} disabled={saving}>
                        {saving ? 'A guardar...' : 'Guardar'}
                    </button>
                </div>
            </div>
        </OrcamentoDialog>
    );
}

/* ============================================================
   EXCEL-STYLE COLUMN FILTER DROPDOWN
   ============================================================ */
function ColFilter({ label, field, data, colFilters, setColFilters, sortCol, setSortCol, sortDir, setSortDir }) {
    const { theme } = useMaquetesTheme();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);
    const dropRef = useRef(null);

    // Fechar ao clicar fora
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (btnRef.current && btnRef.current.contains(e.target)) return;
            if (dropRef.current && dropRef.current.contains(e.target)) return;
            setOpen(false);
            setSearch('');
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const uniqueVals = useMemo(() => {
        const vals = new Set(data.map(o => {
            const v = field === 'margem'
                ? String((parseFloat(o.total_venda||0) - parseFloat(o.total_compra||0)).toFixed(2))
                : String(o[field] || '');
            return v;
        }));
        return [...vals].filter(Boolean).sort((a, b) => a.localeCompare(b, 'pt'));
    }, [data, field]);

    const selected = colFilters[field] || new Set();
    const allSelected = selected.size === 0;

    const filteredVals = uniqueVals.filter(v => v.toLowerCase().includes(search.toLowerCase()));

    const handleOpen = (e) => {
        e.stopPropagation();
        if (open) { setOpen(false); setSearch(''); return; }
        const rect = btnRef.current.getBoundingClientRect();
        setDropPos({ top: Math.min(rect.bottom + 4, Math.max(8, window.innerHeight - 360)), left: Math.max(8, Math.min(rect.left, window.innerWidth - 280)) });
        setOpen(true);
    };

    const toggle = (val) => {
        setColFilters(prev => {
            const cur = new Set(prev[field] || []);
            if (cur.has(val)) cur.delete(val); else cur.add(val);
            if (cur.size === 0 || cur.size === uniqueVals.length) {
                const next = { ...prev }; delete next[field]; return next;
            }
            return { ...prev, [field]: cur };
        });
    };

    const clearFilter = () => {
        setColFilters(prev => { const next = { ...prev }; delete next[field]; return next; });
        setOpen(false); setSearch('');
    };

    const handleSort = (dir) => {
        setSortCol(field); setSortDir(dir); setOpen(false);
    };

    const isFiltered = !allSelected;
    const isSorted = sortCol === field;

    return (
        <div className="col-filter-wrap">
            <button
                ref={btnRef}
                className={`col-filter-btn${isFiltered ? ' filtered' : ''}${isSorted ? ' sorted' : ''}`}
                onClick={handleOpen}
                title={isFiltered ? 'Filtro activo' : 'Filtrar'}
            >
                <LiquidText>{label}</LiquidText>
                <span className="col-filter-arrow">{isFiltered ? '▼' : isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '⌄'}</span>
            </button>
            {open && ReactDOM.createPortal(
                <div
                    ref={dropRef}
                    className="col-filter-dropdown" data-orc-theme={theme} onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } }}
                    style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999 }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="col-filter-sort">
                        <button onClick={() => handleSort('asc')} className={`col-filter-sort-btn${sortCol===field&&sortDir==='asc'?' active':''}`}>↑ Crescente</button>
                        <button onClick={() => handleSort('desc')} className={`col-filter-sort-btn${sortCol===field&&sortDir==='desc'?' active':''}`}>↓ Decrescente</button>
                    </div>
                    <input className="col-filter-search" autoFocus placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} />
                    <div className="col-filter-list">
                        <label className="col-filter-item">
                            <input type="checkbox" checked={allSelected} onChange={clearFilter} />
                            <span>(Todos)</span>
                        </label>
                        {filteredVals.map(v => (
                            <label key={v} className="col-filter-item">
                                <input type="checkbox" checked={selected.has(v)} onChange={() => toggle(v)} />
                                <span>{v || '(vazio)'}</span>
                            </label>
                        ))}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

/* ============================================================
   TAB: LISTA DE ORÇAMENTOS
   ============================================================ */
const ORC_POR_PAGINA = 50;

function TabLista({ filtroEstado, setFiltroEstado, filtroAno, setFiltroAno, onRefreshStats }) {
    const { confirm, notify } = useOrcamentoFeedback();
    const navigate = useNavigate();
    const [filtroSearch, setFiltroSearch] = useState(() => { try { return localStorage.getItem('orc_filtroSearch') || ''; } catch { return ''; } });
    const [filtroDataInicio, setFiltroDataInicio] = useState(() => { try { return localStorage.getItem('orc_filtroDataInicio') || ''; } catch { return ''; } });
    const [filtroDataFim, setFiltroDataFim] = useState(() => { try { return localStorage.getItem('orc_filtroDataFim') || ''; } catch { return ''; } });
    const [page, setPage] = useState(0);
    const [sortCol, setSortCol] = useState('id');
    const [sortDir, setSortDir] = useState('desc');
    const [colFilters, setColFilters] = useState(() => {
        try {
            const raw = localStorage.getItem('orc_colFilters');
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            const result = {};
            for (const [k, v] of Object.entries(parsed)) result[k] = new Set(v);
            return result;
        } catch { return {}; }
    });
    const [copiedId, setCopiedId] = useState(null);

    // Persistir filtros no localStorage
    useEffect(() => { try { localStorage.setItem('orc_filtroSearch', filtroSearch); } catch {} }, [filtroSearch]);
    useEffect(() => { try { localStorage.setItem('orc_filtroDataInicio', filtroDataInicio); } catch {} }, [filtroDataInicio]);
    useEffect(() => { try { localStorage.setItem('orc_filtroDataFim', filtroDataFim); } catch {} }, [filtroDataFim]);
    useEffect(() => {
        try {
            const serializable = {};
            for (const [k, v] of Object.entries(colFilters)) serializable[k] = [...v];
            localStorage.setItem('orc_colFilters', JSON.stringify(serializable));
        } catch {}
    }, [colFilters]);

    const cacheKey = listaCacheKey(filtroAno, filtroEstado, filtroSearch, filtroDataInicio, filtroDataFim);
    const [orcamentos, setOrcamentos] = useState(_cache.listas[cacheKey] || []);
    const [loading, setLoading] = useState(!_cache.listas[cacheKey]);

    // Reset página quando filtros mudam
    useEffect(() => { setPage(0); }, [filtroAno, filtroEstado, filtroSearch, filtroDataInicio, filtroDataFim, colFilters]);

    const fetchOrcamentos = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const params = {};
            if (filtroAno) params.ano = filtroAno;
            if (filtroEstado === 'HOJE') params.hoje = '1';
            else if (filtroEstado) params.estado = filtroEstado;
            if (filtroSearch) params.search = filtroSearch;
            if (filtroDataInicio) params.data_inicio = filtroDataInicio;
            if (filtroDataFim) params.data_fim = filtroDataFim;
            const r = await api.get('/orcamentos', { params });
            _cache.listas[cacheKey] = r.data;
            setOrcamentos(r.data);
        } catch (e) {} finally { if (!silent) setLoading(false); }
    }, [filtroAno, filtroEstado, filtroSearch, filtroDataInicio, filtroDataFim, cacheKey]);

    useEffect(() => {
        if (_cache.listas[cacheKey]) {
            fetchOrcamentos(true);
        } else {
            fetchOrcamentos(false);
        }
    }, [fetchOrcamentos, cacheKey]);

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if (!await confirm('Eliminar este orçamento?')) return;
        try {
            await api.delete(`/orcamentos/${id}`);
            invalidateCache();
            fetchOrcamentos(false);
            onRefreshStats();
        }
        catch (e) { notify(e.response?.data?.error || 'Erro'); }
    };

    // --- Aplicar filtros de coluna + sort ---
    const dadosFiltrados = useMemo(() => {
        let rows = [...orcamentos];

        // Filtros de coluna
        for (const [field, vals] of Object.entries(colFilters)) {
            if (!vals || vals.size === 0) continue;
            rows = rows.filter(o => {
                const v = field === 'margem'
                    ? String((parseFloat(o.total_venda||0) - parseFloat(o.total_compra||0)).toFixed(2))
                    : String(o[field] || '');
                return vals.has(v);
            });
        }

        // Sort
        rows.sort((a, b) => {
            let va, vb;
            if (sortCol === 'margem') {
                va = parseFloat(a.total_venda||0) - parseFloat(a.total_compra||0);
                vb = parseFloat(b.total_venda||0) - parseFloat(b.total_compra||0);
            } else if (['total_compra','total_venda'].includes(sortCol)) {
                va = parseFloat(a[sortCol]||0); vb = parseFloat(b[sortCol]||0);
            } else if (sortCol === 'data') {
                va = a.data || ''; vb = b.data || '';
            } else if (sortCol === 'id') {
                va = a.id; vb = b.id;
            } else {
                va = (a[sortCol] || '').toString().toLowerCase();
                vb = (b[sortCol] || '').toString().toLowerCase();
            }
            if (va < vb) return sortDir === 'asc' ? -1 : 1;
            if (va > vb) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });

        return rows;
    }, [orcamentos, colFilters, sortCol, sortDir]);

    const hasColFilters = Object.keys(colFilters).length > 0;

    const cfProps = { data: orcamentos, colFilters, setColFilters, sortCol, setSortCol, sortDir, setSortDir };

    const handleExportExcel = () => {
        const headers = ['ID', 'Data', 'Cliente', 'Projeto', 'Contacto', 'Gestor', 'Ordem Compra', 'T. Compra (€)', 'T. Venda (€)', 'Margem (€)', 'Estado'];
        const rows = dadosFiltrados.map(o => {
            const margem = parseFloat(o.total_venda || 0) - parseFloat(o.total_compra || 0);
            return [
                o.id,
                fmtDate(o.data),
                o.cliente || '',
                o.projeto || '',
                o.contacto_cliente || '',
                o.gestor_projeto || '',
                o.ordem_compra || '',
                parseFloat(o.total_compra || 0).toFixed(2).replace('.', ','),
                parseFloat(o.total_venda || 0).toFixed(2).replace('.', ','),
                margem.toFixed(2).replace('.', ','),
                ESTADO_LABELS[o.estado] || o.estado || '',
            ];
        });
        const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\r\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orcamentos_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleCopyId = (e, id) => {
        e.stopPropagation();
        navigator.clipboard.writeText(String(id)).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 1500);
        }).catch(() => {});
    };

    return (
        <>
            {/* Filtros top */}
            <div className="orc-filtros">
                <div style={{position:'relative',display:'flex',alignItems:'center',flex:1,minWidth:'200px'}}>
                    <input type="text" placeholder="Pesquisar ID, cliente, projeto, contacto..."
                        value={filtroSearch} onChange={e => setFiltroSearch(e.target.value)} className="orc-input-search" style={{paddingRight: filtroSearch ? '24px' : undefined}} />
                    {filtroSearch && (
                        <button onClick={() => setFiltroSearch('')} style={{position:'absolute',right:'6px',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:'16px',lineHeight:1,padding:0}} title="Limpar pesquisa">×</button>
                    )}
                </div>
                {(filtroEstado || filtroSearch || filtroDataInicio || filtroDataFim || hasColFilters) && (
                    <button className="btn-limpar" onClick={() => {
                        setFiltroEstado(''); setFiltroSearch('');
                        setFiltroDataInicio(''); setFiltroDataFim('');
                        setFiltroAno(String(new Date().getFullYear()));
                        setColFilters({});
                        try { localStorage.removeItem('orc_filtroSearch'); localStorage.removeItem('orc_filtroDataInicio'); localStorage.removeItem('orc_filtroDataFim'); localStorage.removeItem('orc_filtroEstado'); localStorage.removeItem('orc_filtroAno'); localStorage.removeItem('orc_colFilters'); } catch {}
                    }}>
                        Limpar tudo
                    </button>
                )}
                <input type="date" value={filtroDataInicio} onChange={e => setFiltroDataInicio(e.target.value)} className="orc-input-date" />
                <input type="date" value={filtroDataFim} onChange={e => setFiltroDataFim(e.target.value)} className="orc-input-date" />
                <button className="btn-excel-export" onClick={handleExportExcel} title="Exportar para Excel"><LiquidText>
                    📊 Excel
                </LiquidText></button>
            </div>

            {/* Tabela */}
            <div className="orc-table-wrap">
                {loading ? <div className="orc-loading">A carregar...</div> : (
                    <table className="orc-table standard-table">
                        <thead>
                            <tr>
                                <th><ColFilter label="ID"           field="id"               {...cfProps} /></th>
                                <th><ColFilter label="Data"         field="data"             {...cfProps} /></th>
                                <th><ColFilter label="Cliente"      field="cliente"          {...cfProps} /></th>
                                <th><ColFilter label="Projeto"      field="projeto"          {...cfProps} /></th>
                                <th><ColFilter label="Contacto"     field="contacto_cliente" {...cfProps} /></th>
                                <th><ColFilter label="Ordem Compra" field="ordem_compra"     {...cfProps} /></th>
                                <th><ColFilter label="Gestor"       field="gestor_projeto"   {...cfProps} /></th>
                                <th><ColFilter label="T. Compra"    field="total_compra"     {...cfProps} /></th>
                                <th><ColFilter label="T. Venda"     field="total_venda"      {...cfProps} /></th>
                                <th><ColFilter label="Margem"       field="margem"           {...cfProps} /></th>
                                <th><ColFilter label="Estado"       field="estado"           {...cfProps} /></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {dadosFiltrados.length === 0 ? (
                                <tr><td colSpan={12} className="orc-empty">Nenhum orçamento encontrado</td></tr>
                            ) : dadosFiltrados.slice(page * ORC_POR_PAGINA, (page + 1) * ORC_POR_PAGINA).map(o => {
                                const margem = parseFloat(o.total_venda || 0) - parseFloat(o.total_compra || 0);
                                return (
                                    <tr key={o.id} className="orc-row" onClick={() => navigate(`/orcamentos/${o.id}`)}>
                                        <td className="orc-id">{o.id}</td>
                                        <td>{fmtDate(o.data)}</td>
                                        <td className="orc-cliente" title={o.cliente}>{o.cliente}</td>
                                        <td className="orc-projeto">{o.projeto}</td>
                                        <td>{o.contacto_cliente}</td>
                                        <td className="orc-ordem-compra">{o.ordem_compra}</td>
                                        <td className="orc-gestor">{o.gestor_projeto}</td>
                                        <td className="orc-num">{fmt(o.total_compra)}</td>
                                        <td className="orc-num">{fmt(o.total_venda)}</td>
                                        <td className={`orc-num ${margem >= 0 ? 'margem-pos' : 'margem-neg'}`}>{fmt(margem)}</td>
                                        <td>
                                            <span className={`estado-badge ${ESTADO_CLASSES[o.estado] || ''}`}>
                                                {ESTADO_LABELS[o.estado] || o.estado}
                                            </span>
                                        </td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <button className="btn-del-orc" aria-label="Eliminar" onClick={e => handleDelete(o.id, e)} title="Eliminar"><OrcamentoIcon name="trash" fallback="✕" /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Paginação */}
            {dadosFiltrados.length > ORC_POR_PAGINA && (
                <div className="orc-paginacao">
                    <span className="orc-pag-info">
                        {page * ORC_POR_PAGINA + 1}–{Math.min((page + 1) * ORC_POR_PAGINA, dadosFiltrados.length)} de {dadosFiltrados.length}
                    </span>
                    <div className="orc-pag-btns">
                        <button className="pg-arrow" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹</button>
                        {Array.from({ length: Math.ceil(dadosFiltrados.length / ORC_POR_PAGINA) }, (_, idx) => (
                            <button key={idx} className={`pg-num${page === idx ? ' pg-active' : ''}`} onClick={() => setPage(idx)}>{idx + 1}</button>
                        ))}
                        <button className="pg-arrow" onClick={() => setPage(p => Math.min(Math.ceil(orcamentos.length / ORC_POR_PAGINA) - 1, p + 1))} disabled={page === Math.ceil(orcamentos.length / ORC_POR_PAGINA) - 1}>›</button>
                    </div>
                </div>
            )}
        </>
    );
}

/* ============================================================
   TAB: CLIENTES
   ============================================================ */
function TabClientes() {
    const { confirm, notify } = useOrcamentoFeedback();
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null); // null | 'novo' | clienteObj
    const [importing, setImporting] = useState(false);
    const [importMsg, setImportMsg] = useState('');
    const fileRef = useRef();

    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/clientes', { params: search ? { search } : {} });
            setClientes(Array.isArray(r.data) ? r.data : []);
        } catch (e) {} finally { setLoading(false); }
    }, [search]);

    useEffect(() => { fetch(); }, [fetch]);

    const handleDelete = async (id) => {
        if (!await confirm('Remover este cliente?')) return;
        try { await api.delete(`/clientes/${id}`); fetch(); }
        catch (e) { notify(e.response?.data?.error || 'Erro'); }
    };

    const handleImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true); setImportMsg('');
        try {
            const fd = new FormData();
            fd.append('ficheiro', file);
            const r = await api.post('/clientes/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setImportMsg(r.data.message);
            fetch();
        } catch (err) {
            setImportMsg('Erro: ' + (err.response?.data?.error || err.message));
        } finally {
            setImporting(false);
            e.target.value = '';
        }
    };

    return (
        <div>
            <div className="tab-toolbar">
                <input type="text" placeholder="Pesquisar clientes..." value={search}
                    onChange={e => setSearch(e.target.value)} className="orc-input-search" style={{ maxWidth: 300 }} />
                <div className="tab-toolbar-right">
                    <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleImport} />
                    <button className="btn-secondary-action" onClick={() => fileRef.current.click()} disabled={importing}>
                        {importing ? 'A importar...' : 'Importar Excel/CSV'}
                    </button>
                    <button className="btn-secondary-action" onClick={async () => {
                        try {
                            const r = await api.get('/clientes/template', { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([r.data]));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'template_clientes.xlsx';
                            a.click();
                            window.URL.revokeObjectURL(url);
                        } catch (e) { notify('Erro ao descarregar template'); }
                    }}>Template Excel</button>
                    <button className="btn-novo-item" onClick={() => setModal('novo')}><OrcamentoIcon name="plus" fallback="+" /> Novo Cliente</button>
                </div>
            </div>
            {importMsg && <div className={`import-msg ${importMsg.startsWith('Erro') ? 'import-err' : 'import-ok'}`}>{importMsg}</div>}
            <div className="orc-table-wrap">
                {loading ? <div className="orc-loading">A carregar...</div> : (
                    <table className="orc-table standard-table orc-entities-table">
                        <thead>
                            <tr><th>ID</th><th>Nome</th><th>Tipo</th><th>NIF</th><th>Email</th><th>Telefone</th><th>Contacto</th><th></th></tr>
                        </thead>
                        <tbody>
                            {clientes.length === 0 ? (
                                <tr><td colSpan={8} className="orc-empty">Nenhum cliente encontrado</td></tr>
                            ) : clientes.map(c => (
                                <tr key={c.id} className="orc-row" onClick={() => setModal(c)}>
                                    <td className="orc-id">{c.id}</td>
                                    <td><strong>{c.nome}</strong></td>
                                    <td>{c.tipo}</td>
                                    <td>{c.nif}</td>
                                    <td>{c.email}</td>
                                    <td>{c.telefone}</td>
                                    <td>{c.contacto}</td>
                                    <td onClick={e => e.stopPropagation()}>
                                        <button className="btn-del-orc" aria-label="Eliminar" onClick={() => handleDelete(c.id)}><OrcamentoIcon name="trash" fallback="✕" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {modal && (
                <ModalCliente
                    cliente={modal === 'novo' ? null : modal}
                    onClose={() => setModal(null)}
                    onSaved={fetch}
                />
            )}
        </div>
    );
}

/* ============================================================
   TAB: FORNECEDORES
   ============================================================ */
function TabFornecedores() {
    const { confirm, notify } = useOrcamentoFeedback();
    const [fornecedores, setFornecedores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [importing, setImporting] = useState(false);
    const [search, setSearch] = useState('');
    const fileInputRef = useRef(null);

    const fetchForn = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/fornecedores');
            setFornecedores(Array.isArray(r.data) ? r.data : []);
        } catch (e) {} finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchForn(); }, [fetchForn]);

    const handleDelete = async (id) => {
        if (!await confirm('Remover este fornecedor?')) return;
        try { await api.delete(`/fornecedores/${id}`); fetchForn(); }
        catch (e) { notify(e.response?.data?.error || 'Erro'); }
    };

    const handleDownloadTemplate = async () => {
        try {
            const r = await api.get('/fornecedores/template', { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = 'template_fornecedores.xlsx'; a.click();
            URL.revokeObjectURL(url);
        } catch { notify('Erro ao descarregar template'); }
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('ficheiro', file);
            const r = await api.post('/fornecedores/import', fd);
            notify(r.data.message);
            fetchForn();
        } catch (err) {
            notify(err.response?.data?.error || 'Erro ao importar');
        } finally {
            setImporting(false);
            e.target.value = '';
        }
    };

    const fornecedoresFiltrados = fornecedores.filter(f => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (f.nome || '').toLowerCase().includes(q)
            || (f.abreviatura || '').toLowerCase().includes(q)
            || (f.nif || '').includes(q)
            || (f.email || '').toLowerCase().includes(q)
            || (f.localidade || '').toLowerCase().includes(q);
    });

    return (
        <div>
            <div className="tab-toolbar">
                <input type="text" placeholder="Pesquisar fornecedores..." value={search}
                    onChange={e => setSearch(e.target.value)} className="orc-input-search" style={{ maxWidth: 300 }} />
                <div className="tab-toolbar-right">
                    <button className="btn-secondary-action" onClick={handleDownloadTemplate}>Template</button>
                    <button className="btn-secondary-action" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                        {importing ? 'A importar...' : 'Importar Excel'}
                    </button>
                    <input ref={fileInputRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }} onChange={handleImport} />
                    <button className="btn-novo-item" onClick={() => setModal('novo')}><OrcamentoIcon name="plus" fallback="+" /> Novo Fornecedor</button>
                </div>
            </div>
            <div className="orc-table-wrap">
                {loading ? <div className="orc-loading">A carregar...</div> : (
                    <table className="orc-table standard-table orc-entities-table">
                        <thead>
                            <tr><th>ID</th><th>Nome</th><th>Abreviatura</th><th>NIF</th><th>Email</th><th>Telefone</th><th>Localidade</th><th>Condição Pag.</th><th></th></tr>
                        </thead>
                        <tbody>
                            {fornecedoresFiltrados.length === 0 ? (
                                <tr><td colSpan={9} className="orc-empty">Nenhum fornecedor</td></tr>
                            ) : fornecedoresFiltrados.map(f => (
                                <tr key={f.id} className="orc-row" onClick={() => setModal(f)}>
                                    <td className="orc-id">{f.id}</td>
                                    <td><strong>{f.nome}</strong></td>
                                    <td>{f.abreviatura}</td>
                                    <td>{f.nif}</td>
                                    <td>{f.email}</td>
                                    <td>{f.telefone}</td>
                                    <td>{f.localidade}</td>
                                    <td>{f.condicao_pagamento && <span className="orc-neutral-pill">{f.condicao_pagamento}</span>}</td>
                                    <td onClick={e => e.stopPropagation()}>
                                        <button className="btn-del-orc" aria-label="Eliminar" onClick={() => handleDelete(f.id)}><OrcamentoIcon name="trash" fallback="✕" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {modal && (
                <ModalFornecedor
                    fornecedor={modal === 'novo' ? null : modal}
                    onClose={() => setModal(null)}
                    onSaved={fetchForn}
                />
            )}
        </div>
    );
}

/* ============================================================
   TAB: NOTAS DE ENCOMENDA (EF)
   ============================================================ */
const EF_ESTADO_CLS = { PENDENTE: 'ef-estado-PENDENTE', ENVIADA: 'ef-estado-ENVIADA', CONFIRMADA: 'ef-estado-CONFIRMADA' };

function ModalEditEF({ ef, onClose, onSaved }) {
    const { confirm, notify } = useOrcamentoFeedback();
    const [form, setForm] = useState({
        fornecedor: ef.fornecedor || '',
        descricao: ef.descricao || '',
        qtd: ef.qtd !== null && ef.qtd !== undefined ? (() => { const n = parseFloat(ef.qtd); return isNaN(n) ? '' : (n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1)); })() : '',
        valor_unitario: formatarUnitario(ef.valor_unitario),
        valor_total: ef.valor_total !== null && ef.valor_total !== undefined ? parseFloat(ef.valor_total).toFixed(2) : '',
        notas: ef.notas || '',
    });
    const [fornecedores, setFornecedores] = useState([]);
    const [saving, setSaving] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        api.get('/fornecedores').then(r => setFornecedores(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    const recalc = (field, value) => {
        const next = { ...form, [field]: value };
        const q = parseFloat(String(field === 'qtd' ? value : next.qtd).replace(',', '.')) || 0;
        const vu = parseFloat(String(field === 'valor_unitario' ? value : next.valor_unitario).replace(',', '.')) || 0;
        if (field === 'qtd' || field === 'valor_unitario') {
            next.valor_total = totalLinha(q, vu);
        }
        setForm(next);
    };

    const handleDuplicar = async () => {
        setDuplicating(true); setErr('');
        try {
            const payload = {
                orcamento_id: ef.orcamento_id,
                fornecedor: form.fornecedor,
                descricao: form.descricao,
                qtd: parseFloat(String(form.qtd).replace(',', '.')) || 0,
                valor_unitario: parseFloat(String(form.valor_unitario).replace(',', '.')) || 0,
                valor_total: parseFloat(String(form.valor_total).replace(',', '.')) || 0,
                notas: form.notas,
            };
            await api.post('/encomendas-fornecedor', payload);
            onSaved();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || e.message || 'Erro ao duplicar');
        } finally { setDuplicating(false); }
    };

    const handleSave = async () => {
        setSaving(true); setErr('');
        try {
            const payload = {
                ...form,
                qtd: parseFloat(String(form.qtd).replace(',', '.')) || 0,
                valor_unitario: parseFloat(String(form.valor_unitario).replace(',', '.')) || 0,
                valor_total: parseFloat(String(form.valor_total).replace(',', '.')) || 0,
            };
            await api.put(`/encomendas-fornecedor/${ef.id}`, payload);
            onSaved();
            onClose();
        } catch (e) {
            console.error('Erro ao guardar EF:', e);
            setErr(e.response?.data?.error || e.message || 'Erro ao guardar');
        } finally { setSaving(false); }
    };

    const [deleting, setDeleting] = useState(false);
    const handleApagar = async () => {
        if (!await confirm(`Apagar a encomenda ${ef.numero}?\n\nEsta ação é definitiva.`)) return;
        setDeleting(true); setErr('');
        try {
            await api.delete(`/encomendas-fornecedor/${ef.id}`);
            onSaved();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || e.message || 'Erro ao apagar');
        } finally { setDeleting(false); }
    };

    return (
        <OrcamentoDialog onClose={onClose}>
            <div className="modal-ef" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div className="modal-ef-header">
                    <div>
                        <span className="modal-ef-num">{ef.numero}</span>
                        <span className={`ef-estado ${ef.tem_fatura_ligada ? 'ef-estado-associada' : 'ef-estado-aguarda'}`}>{ef.tem_fatura_ligada ? 'Associada' : 'Aguarda Fatura'}</span>
                    </div>
                    <button className="modal-close" aria-label="Fechar" onClick={onClose}><OrcamentoIcon name="close" fallback="✕" /></button>
                </div>
                <div className="modal-form-body">
                    <div className="modal-form-grid">
                        <div className="modal-field full">
                            <label>FORNECEDOR</label>
                            <select value={form.fornecedor} onChange={e => {
                                const f = fornecedores.find(f => f.nome === e.target.value);
                                setForm({ ...form, fornecedor: e.target.value, email_fornecedor: f?.email || form.email_fornecedor });
                            }}>
                                <option value="">-- Selecionar --</option>
                                {fornecedores.map(f => <option key={f.id} value={f.nome}>{f.nome}</option>)}
                            </select>
                        </div>
                        <div className="modal-field full">
                            <label>DESCRIÇÃO</label>
                            <input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} />
                        </div>
                        <div className="modal-field">
                            <label>QTD</label>
                            <input type="number" step="0.1" min="0" value={form.qtd}
                                onChange={e => recalc('qtd', e.target.value)}
                                onBlur={e => {
                                    const v = parseFloat(e.target.value);
                                    if (!isNaN(v)) recalc('qtd', v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1));
                                }} />
                        </div>
                        <div className="modal-field">
                            <label>V. UNITÁRIO (s/ IVA)</label>
                            <input type="number" step="0.0001" min="0" value={form.valor_unitario}
                                onChange={e => recalc('valor_unitario', e.target.value)} />
                        </div>
                        <div className="modal-field">
                            <label>TOTAL (s/ IVA)</label>
                            <input type="number" step="0.01" min="0" value={form.valor_total}
                                onChange={e => setForm({ ...form, valor_total: e.target.value })} />
                        </div>
                        <div className="modal-field">
                            <label>ESTADO</label>
                            <div style={{ paddingTop: 6 }}>
                                <span className={`ef-estado ${ef.tem_fatura_ligada ? 'ef-estado-associada' : 'ef-estado-aguarda'}`}>
                                    {ef.tem_fatura_ligada ? 'Associada' : 'Aguarda Fatura'}
                                </span>
                            </div>
                        </div>
                        <div className="modal-field full">
                            <label>NOTAS</label>
                            <textarea rows={2} value={form.notas}
                                onChange={e => setForm({ ...form, notas: e.target.value })} />
                        </div>
                    </div>
                    {err && <div className="modal-err">{err}</div>}
                    <div className="modal-form-actions" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
                        <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', borderBottom: '1px solid #f1f5f9', paddingBottom: 10 }}>
                            <button className="btn-modal-cancel" style={{ whiteSpace: 'nowrap' }} onClick={async () => {
                                try {
                                    const token = localStorage.getItem('token');
                                    const res = await fetch(`${api.defaults.baseURL}/encomendas-fornecedor/${ef.id}/documento?formato=pdf`, { headers: { 'Authorization': `Bearer ${token}` } });
                                    if (!res.ok) throw new Error();
                                    window.open(URL.createObjectURL(await res.blob()), '_blank');
                                } catch { notify('Erro ao gerar documento'); }
                            }}><OrcamentoIcon name="print" fallback="🖨️" /> Imprimir</button>
                            <button className="btn-modal-cancel" style={{ whiteSpace: 'nowrap' }} onClick={async () => {
                                try {
                                    const token = localStorage.getItem('token');
                                    const res = await fetch(`${api.defaults.baseURL}/encomendas-fornecedor/${ef.id}/documento?formato=pdf`, { headers: { 'Authorization': `Bearer ${token}` } });
                                    if (!res.ok) throw new Error();
                                    const a = document.createElement('a'); a.href = URL.createObjectURL(await res.blob()); a.download = `encomenda_${ef.numero}.pdf`; a.click();
                                } catch { notify('Erro ao descarregar PDF'); }
                            }}><OrcamentoIcon name="document" fallback="📄" /> PDF</button>
                            <button className="btn-modal-cancel" style={{ whiteSpace: 'nowrap' }} onClick={handleDuplicar} disabled={duplicating} title="Criar nova encomenda com os mesmos dados">
                                {duplicating ? 'A duplicar...' : <><OrcamentoIcon name="copy" fallback="⧉" /> Duplicar</>}
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
                            <button className="btn-modal-danger orc-delete-icon" onClick={handleApagar} disabled={deleting || ef.tem_fatura_ligada}
                                aria-label={deleting ? 'A apagar encomenda' : 'Apagar encomenda'} aria-busy={deleting}
                                title={ef.tem_fatura_ligada ? 'Tem fatura associada — desassocie primeiro' : 'Apagar esta encomenda'}>
                                <OrcamentoIcon name="trash" fallback="🗑" />
                            </button>
                            <div data-liquid-actions="true" style={{ display: 'flex', gap: 8 }}>
                                <button className="btn-modal-cancel" style={{ whiteSpace: 'nowrap' }} onClick={onClose}>Cancelar</button>
                                <button className="btn-modal-save" style={{ whiteSpace: 'nowrap' }} onClick={handleSave} disabled={saving}>
                                    {saving ? 'A guardar...' : 'Guardar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </OrcamentoDialog>
    );
}

function ModalNovaEF({ onClose, onSaved }) {
    const [form, setForm] = useState({
        orcamento_id: '',
        fornecedor: '',
        email_fornecedor: '',
        descricao: '',
        qtd: '',
        valor_unitario: '',
        valor_total: '',
    });
    const [fornecedores, setFornecedores] = useState([]);
    const [orcInfo, setOrcInfo] = useState(null);
    const [loadingOrc, setLoadingOrc] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        api.get('/fornecedores').then(r => setFornecedores(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    const buscarOrcamento = async (id) => {
        if (!id) { setOrcInfo(null); return; }
        setLoadingOrc(true);
        try {
            const r = await api.get(`/orcamentos/${id}`);
            setOrcInfo(r.data);
            setErr('');
        } catch {
            setOrcInfo(null);
            setErr('Orçamento não encontrado');
        } finally { setLoadingOrc(false); }
    };

    const recalc = (field, value) => {
        const next = { ...form, [field]: value };
        const q = parseFloat(String(field === 'qtd' ? value : next.qtd).replace(',', '.')) || 0;
        const vu = parseFloat(String(field === 'valor_unitario' ? value : next.valor_unitario).replace(',', '.')) || 0;
        if (field === 'qtd' || field === 'valor_unitario') {
            next.valor_total = totalLinha(q, vu);
        }
        setForm(next);
    };

    const handleSave = async () => {
        if (!form.orcamento_id) { setErr('Insira o número do orçamento'); return; }
        if (!orcInfo) { setErr('Orçamento não encontrado. Verifique o número.'); return; }
        if (!form.fornecedor) { setErr('Selecione um fornecedor'); return; }
        setSaving(true); setErr('');
        try {
            const { email_fornecedor, ...rest } = form;
            const payload = {
                ...rest,
                orcamento_id: parseInt(form.orcamento_id),
                qtd: parseFloat(String(form.qtd).replace(',', '.')) || 0,
                valor_unitario: parseFloat(String(form.valor_unitario).replace(',', '.')) || 0,
                valor_total: parseFloat(String(form.valor_total).replace(',', '.')) || 0,
            };
            await api.post('/encomendas-fornecedor', payload);
            onSaved();
            onClose();
        } catch (e) {
            console.error('Erro ao criar EF:', e);
            setErr(e.response?.data?.error || e.message || 'Erro ao criar encomenda');
        } finally { setSaving(false); }
    };

    return (
        <OrcamentoDialog onClose={onClose}>
            <div className="modal-ef" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div className="modal-ef-header">
                    <div><span className="modal-ef-num">Nova Encomenda a Fornecedor</span></div>
                    <button className="modal-close" aria-label="Fechar" onClick={onClose}><OrcamentoIcon name="close" fallback="✕" /></button>
                </div>
                <div className="modal-form-body">
                    <div className="modal-form-grid">
                        <div className="modal-field" style={{ position: 'relative' }}>
                            <label>Nº ORÇAMENTO *</label>
                            <input type="number" min="1" placeholder="Ex: 1, 2, 3..."
                                value={form.orcamento_id}
                                onChange={e => {
                                    setForm({ ...form, orcamento_id: e.target.value });
                                    setOrcInfo(null);
                                    setErr('');
                                }}
                                onBlur={e => buscarOrcamento(e.target.value)} />
                            {loadingOrc && <span style={{ fontSize: 11, color: '#888' }}>A verificar...</span>}
                        </div>
                        <div className="modal-field">
                            <label>CLIENTE</label>
                            <input type="text" readOnly value={orcInfo ? (orcInfo.cliente || '') : ''} style={{ background: '#f5f5f5' }} />
                        </div>
                        <div className="modal-field full">
                            <label>PROJETO</label>
                            <input type="text" readOnly value={orcInfo ? (orcInfo.projeto || '') : ''} style={{ background: '#f5f5f5' }} />
                        </div>
                        <div className="modal-field full">
                            <label>FORNECEDOR *</label>
                            <select value={form.fornecedor} onChange={e => {
                                const f = fornecedores.find(f => f.nome === e.target.value);
                                setForm({ ...form, fornecedor: e.target.value, email_fornecedor: f?.email || '' });
                            }}>
                                <option value="">-- Selecionar --</option>
                                {fornecedores.map(f => <option key={f.id} value={f.nome}>{f.nome}</option>)}
                            </select>
                        </div>
                        <div className="modal-field full">
                            <label>DESCRIÇÃO</label>
                            <input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} />
                        </div>
                        <div className="modal-field">
                            <label>QTD</label>
                            <input type="number" step="0.1" min="0" value={form.qtd}
                                onChange={e => recalc('qtd', e.target.value)}
                                onBlur={e => {
                                    const v = parseFloat(e.target.value);
                                    if (!isNaN(v)) recalc('qtd', v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1));
                                }} />
                        </div>
                        <div className="modal-field">
                            <label>V. UNITÁRIO (s/ IVA)</label>
                            <input type="number" step="0.0001" min="0" value={form.valor_unitario}
                                onChange={e => recalc('valor_unitario', e.target.value)} />
                        </div>
                        <div className="modal-field">
                            <label>TOTAL (s/ IVA)</label>
                            <input type="number" step="0.01" min="0" value={form.valor_total}
                                onChange={e => setForm({ ...form, valor_total: e.target.value })} />
                        </div>
                        <div className="modal-field full">
                            {/* Email fornecedor removido */}
                        </div>
                    </div>
                    {err && <div className="modal-err">{err}</div>}
                    <div className="modal-form-actions">
                        <button className="btn-modal-cancel" onClick={onClose}>Cancelar</button>
                        <button className="btn-modal-save" onClick={handleSave} disabled={saving}>
                            {saving ? 'A criar...' : 'Criar Encomenda'}
                        </button>
                    </div>
                </div>
            </div>
        </OrcamentoDialog>
    );
}

const PAGE_SIZE_EF_ORC = 200;

/* Número(s) da(s) fatura(s) ligada(s) a uma EF.
   O servidor devolve a lista completa: a maioria das EFs tem uma fatura, mas
   há-as repartidas por várias e nesse caso interessa vê-las todas.
   Se houver ligação sem número (fatura ainda sem nº de documento), fica uma
   etiqueta genérica em vez de um traço, para não parecer que não tem fatura. */
function renderFaturasEF(ef) {
    const lista = Array.isArray(ef.faturas) ? ef.faturas.filter(f => f && f.numero) : [];
    if (lista.length > 0) {
        return (
            <div className="orc-fat-cel">
                {lista.map(f => (
                    <span key={f.id} className="orc-fat-tag"
                        title={f.data ? `Fatura de ${new Date(f.data).toLocaleDateString('pt-PT')}` : ''}>
                        {f.numero}
                    </span>
                ))}
            </div>
        );
    }
    if (ef.numero_fatura_fornecedor) {
        return <div className="orc-fat-cel"><span className="orc-fat-tag">{ef.numero_fatura_fornecedor}</span></div>;
    }
    if (ef.tem_fatura_ligada) {
        return <div className="orc-fat-cel"><span className="orc-fat-tag orc-fat-tag-generica">Fatura</span></div>;
    }
    return <span className="orc-fat-vazio">—</span>;
}

/* Filtro de coluna para uma tabela paginada no servidor.

   O ColFilter das outras tabelas monta a lista de valores a partir das linhas
   que tem em mãos. Aqui isso não serve: são 7610 encomendas e o ecrã só tem a
   página atual, portanto a lista de valores viria com uma amostra e o filtro
   parecia estar avariado. Este vai buscar os valores distintos ao servidor, e
   quem filtra e ordena é também o servidor. Reaproveita as classes de CSS do
   outro para os dois terem o mesmo aspeto. */
function ColFilterEF({ label, campo, colFilters, setColFilters, sortCol, setSortCol, sortDir, setSortDir, alinha }) {
    const { theme } = useMaquetesTheme();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [valores, setValores] = useState([]);
    const [truncado, setTruncado] = useState(false);
    const [aCarregar, setACarregar] = useState(false);
    const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);
    const dropRef = useRef(null);

    const selected = colFilters[campo] || new Set();
    const isFiltered = selected.size > 0;
    const isSorted = sortCol === campo;

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (btnRef.current && btnRef.current.contains(e.target)) return;
            if (dropRef.current && dropRef.current.contains(e.target)) return;
            setOpen(false); setSearch('');
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // A pesquisa vai ao servidor, com uma pausa para não disparar a cada tecla.
    useEffect(() => {
        if (!open) return;
        let vivo = true;
        setACarregar(true);
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/encomendas-fornecedor/valores/${campo}`, {
                    params: { q: search || undefined },
                });
                if (!vivo) return;
                setValores(r.data?.valores || []);
                setTruncado(!!r.data?.truncado);
            } catch { if (vivo) { setValores([]); setTruncado(false); } }
            finally { if (vivo) setACarregar(false); }
        }, search ? 300 : 0);
        return () => { vivo = false; clearTimeout(t); };
    }, [open, search, campo]);

    const abrir = (e) => {
        e.stopPropagation();
        if (open) { setOpen(false); setSearch(''); return; }
        const rect = btnRef.current.getBoundingClientRect();
        setDropPos({ top: Math.min(rect.bottom + 4, Math.max(8, window.innerHeight - 360)), left: Math.max(8, Math.min(rect.left, window.innerWidth - 280)) });
        setOpen(true);
    };

    const alternar = (v) => {
        setColFilters(prev => {
            const cur = new Set(prev[campo] || []);
            if (cur.has(v)) cur.delete(v); else cur.add(v);
            const next = { ...prev };
            if (cur.size === 0) delete next[campo]; else next[campo] = cur;
            return next;
        });
    };

    const limpar = () => {
        setColFilters(prev => { const n = { ...prev }; delete n[campo]; return n; });
        setOpen(false); setSearch('');
    };

    const ordenar = (dir) => { setSortCol(campo); setSortDir(dir); setOpen(false); };

    return (
        <div className="col-filter-wrap" style={alinha ? { justifyContent: alinha } : undefined}>
            <button ref={btnRef}
                className={`col-filter-btn${isFiltered ? ' filtered' : ''}${isSorted ? ' sorted' : ''}`}
                onClick={abrir}
                title={isFiltered ? `${selected.size} valor(es) selecionado(s)` : 'Filtrar e ordenar'}>
                <LiquidText>{label}</LiquidText>
                <span className="col-filter-arrow">
                    {isFiltered ? '▼' : isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '⌄'}
                </span>
            </button>
            {open && ReactDOM.createPortal(
                <div ref={dropRef} className="col-filter-dropdown" data-orc-theme={theme} onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } }}
                    style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999 }}
                    onClick={e => e.stopPropagation()}>
                    <div className="col-filter-sort">
                        <button onClick={() => ordenar('asc')}
                            className={`col-filter-sort-btn${isSorted && sortDir === 'asc' ? ' active' : ''}`}>↑ Crescente</button>
                        <button onClick={() => ordenar('desc')}
                            className={`col-filter-sort-btn${isSorted && sortDir === 'desc' ? ' active' : ''}`}>↓ Decrescente</button>
                    </div>
                    <input className="col-filter-search" autoFocus placeholder="Pesquisar..."
                        value={search} onChange={e => setSearch(e.target.value)} />
                    <div className="col-filter-list">
                        <label className="col-filter-item">
                            <input type="checkbox" checked={!isFiltered} onChange={limpar} />
                            <span>(Todos)</span>
                        </label>
                        {aCarregar && <div className="col-filter-nota">A carregar…</div>}
                        {!aCarregar && valores.length === 0 && <div className="col-filter-nota">Sem valores</div>}
                        {valores.map(v => (
                            <label key={v} className="col-filter-item">
                                <input type="checkbox" checked={selected.has(v)} onChange={() => alternar(v)} />
                                <span>{v}</span>
                            </label>
                        ))}
                        {/* Dizer que a lista foi cortada, em vez de deixar pensar
                            que aqueles são todos os valores que existem. */}
                        {truncado && (
                            <div className="col-filter-nota">
                                Mais de 500 valores — use a pesquisa para chegar ao que procura.
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

function TabEncomendasFornecedor({ autoOpenNova }) {
    const { confirm, notify } = useOrcamentoFeedback();
    const [deletingId, setDeletingId] = useState(null);
    const deleteBusy = useRef(false);
    const handleApagarEncomenda = async (ef) => {
        if (deleteBusy.current || ef.tem_fatura_ligada) return;
        deleteBusy.current = true;
        setDeletingId(ef.id);
        try {
            if (!await confirm(`Apagar a encomenda ${ef.numero}?\n\nEsta ação é definitiva.`)) return;
            await api.delete(`/encomendas-fornecedor/${ef.id}`);
            // Se a última linha da página for eliminada, regressar à anterior.
            if (rows.length === 1 && page > 1) setPage(p => p - 1);
            else await fetchRows();
        } catch (e) {
            await notify(e.response?.data?.error || e.message || 'Erro ao apagar encomenda');
        } finally {
            deleteBusy.current = false;
            setDeletingId(null);
        }
    };


    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('');
    const [page, setPage] = useState(1);
    const [modalEf, setModalEf] = useState(null);
    // Filtros e ordenação por coluna. Aplicados no servidor, sobre a lista toda.
    const [colFilters, setColFilters] = useState({});
    const [sortCol, setSortCol] = useState('');
    const [sortDir, setSortDir] = useState('desc');
    const [modalNova, setModalNova] = useState(false);
    const [modalImport, setModalImport] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(() => {
        try {
            const s = sessionStorage.getItem('ef_importResult');
            if (!s) return null;
            const d = JSON.parse(s);
            // Normalizar campo legado
            if (d && d.fornecedores_criados && !d.fornecedores_sem_match) {
                d.fornecedores_sem_match = d.fornecedores_criados;
                delete d.fornecedores_criados;
            }
            return d;
        } catch { return null; }
    });
    const [importError, setImportError] = useState('');
    const [fornecedoresBD, setFornecedoresBD] = useState([]);
    const [matchSelections, setMatchSelections] = useState({});
    const [matchApplying, setMatchApplying] = useState({});

    const handleImport = async () => {
        if (!importFile) return;
        setImportLoading(true);
        setImportError('');
        setImportResult(null);
        try {
            const fd = new FormData();
            fd.append('ficheiro', importFile);
            const r = await api.post('/encomendas-fornecedor/importar-excel', fd, { timeout: 120000 });
            setImportResult(r.data);
            try { sessionStorage.setItem('ef_importResult', JSON.stringify(r.data)); } catch {}
            fetchRows();
        } catch (e) {
            setImportError(e.response?.data?.error || e.message);
        } finally {
            setImportLoading(false);
        }
    };

    const handleDesfazer = async () => {
        if (!importResult?.ids?.length) return;
        if (!await confirm(`Apagar ${importResult.ids.length} registos importados?`)) return;
        try {
            await api.delete('/encomendas-fornecedor/importar-excel/desfazer', { data: { ids: importResult.ids } });
            setImportResult(null);
            setImportFile(null);
            try { sessionStorage.removeItem('ef_importResult'); } catch {}
            fetchRows();
            notify('Importação desfeita.');
        } catch (e) {
            notify('Erro: ' + (e.response?.data?.error || e.message));
        }
    };

    const handleReprocessar2025 = async () => {
        if (!await confirm('Reprocessar todas as EFs de 2025: ligar a orçamentos e corrigir estados?')) return;
        try {
            const r = await api.post('/encomendas-fornecedor/importar-excel/reprocessar-2025', {}, { timeout: 120000 });
            notify(`✓ ${r.data.actualizadas_estado} estados corrigidos, ${r.data.ligadas_orcamento} ligadas a orçamentos, ${r.data.datas_fatura_limpas} datas incorrectas removidas, ${r.data.nifs_actualizados} NIFs actualizados, ${r.data.valor_unitario_corrigido} valores unitários corrigidos.`);
            fetchRows();
        } catch (e) {
            notify('Erro: ' + (e.response?.data?.error || e.message));
        }
    };

    const handleResetFaturas2025 = async () => {
        if (!await confirm('ATENÇÃO: Vai limpar o número de fatura e data de fatura de TODAS as EF-2025. Fazer isto antes de reimportar o Excel. Continuar?')) return;
        try {
            const r = await api.post('/encomendas-fornecedor/importar-excel/reset-faturas-2025', {}, { timeout: 60000 });
            notify(`✓ ${r.data.resetadas} EFs de 2025 limpas. Agora reimporta o Excel para carregar os dados correctos.`);
            fetchRows();
        } catch (e) {
            notify('Erro: ' + (e.response?.data?.error || e.message));
        }
    };

    // Carregar fornecedores da BD quando há não-cruzados
    useEffect(() => {
        const semMatch = importResult?.fornecedores_sem_match;
        if (semMatch?.length > 0 && fornecedoresBD.length === 0) {
            api.get('/fornecedores').then(r => setFornecedoresBD(r.data || [])).catch(() => {});
        }
    }, [importResult, fornecedoresBD.length]);

    const handleMatchAplicar = async (nomeExcel) => {
        const fornecedorId = matchSelections[nomeExcel];
        if (!fornecedorId) return;
        setMatchApplying(prev => ({ ...prev, [nomeExcel]: true }));
        try {
            const r = await api.patch('/encomendas-fornecedor/importar-excel/match-fornecedor', { nome_excel: nomeExcel, fornecedor_id: fornecedorId });
            // Remover da lista de não-cruzados
            setImportResult(prev => {
                const updated = { ...prev, fornecedores_sem_match: (prev.fornecedores_sem_match || []).filter(n => n !== nomeExcel) };
                try { sessionStorage.setItem('ef_importResult', JSON.stringify(updated)); } catch {}
                return updated;
            });
            fetchRows();
        } catch (e) {
            notify('Erro: ' + (e.response?.data?.error || e.message));
        } finally {
            setMatchApplying(prev => ({ ...prev, [nomeExcel]: false }));
        }
    };

    useEffect(() => { if (autoOpenNova) setModalNova(true); }, [autoOpenNova]);

    // Cada pedido leva um número de ordem, e só o último manda no que se mostra.
    // Sem isto, a lista sem filtro (~0,9 s, é a mais pesada) chegava depois da
    // pesquisa (~0,1 s) e escrevia-lhe por cima: escrevias "16087" e ficavas a
    // ver as 7528 encomendas todas, como se a pesquisa não tivesse funcionado.
    const pedidoRef = useRef(0);
    const fetchRows = useCallback(async () => {
        const meu = ++pedidoRef.current;
        setLoading(true);
        try {
            const params = { page, pageSize: PAGE_SIZE_EF_ORC };
            if (search) params.search = search;
            if (filtroEstado === 'associada') params.tem_fatura = 'true';
            else if (filtroEstado === 'aguarda') params.tem_fatura = 'false';
            // Os Set vão como listas; sem chaves, não se manda o parâmetro.
            const cols = Object.fromEntries(
                Object.entries(colFilters).map(([k, v]) => [k, [...v]]).filter(([, v]) => v.length));
            if (Object.keys(cols).length) params.filtros = JSON.stringify(cols);
            if (sortCol) { params.sort = sortCol; params.dir = sortDir; }
            const r = await api.get('/encomendas-fornecedor', { params });
            if (meu !== pedidoRef.current) return;   // resposta atrasada: ignora-se
            setRows(r.data.rows || []);
            setTotal(r.data.total || 0);
        } catch (e) {} finally { if (meu === pedidoRef.current) setLoading(false); }
    }, [search, filtroEstado, page, colFilters, sortCol, sortDir]);

    // Reset para página 1 quando filtros mudam
    useEffect(() => { setPage(1); }, [search, filtroEstado, colFilters, sortCol, sortDir]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const filtered = rows; // filtragem feita no servidor
    const totalPages = Math.ceil(total / PAGE_SIZE_EF_ORC);

    const [expandedEfRows, setExpandedEfRows] = useState(new Set());

    return (
        <>
            <div className="tab-toolbar ef-toolbar">
                <div className="ef-toolbar-filters" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input className="orc-input-search" placeholder="Pesquisar nº, fornecedor, descrição..."
                        value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 260 }} />
                    <div className="gf-btn-group">
                        <button className={`gf-btn-s ${filtroEstado === '' ? 'active' : ''}`} onClick={() => setFiltroEstado('')}>Todas</button>
                        <button className={`gf-btn-s ${filtroEstado === 'aguarda' ? 'active' : ''}`} onClick={() => setFiltroEstado('aguarda')}>Aguarda Fatura</button>
                        <button className={`gf-btn-s ${filtroEstado === 'associada' ? 'active' : ''}`} onClick={() => setFiltroEstado('associada')}>Associadas</button>
                    </div>
                </div>
                <div className="ef-toolbar-actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className="ef-toolbar-count" style={{ fontSize: 12, color: '#888' }}>{total} encomenda{total !== 1 ? 's' : ''}</span>
                    {importResult?.ids?.length > 0 && (
                        <button onClick={handleDesfazer} className="ef-action ef-action-danger ef-action-undo" title={`Reverter ${importResult.ids.length} registos importados`}><LiquidText>↩ Reverter importação</LiquidText></button>
                    )}
                    <button className="btn-novo-item" onClick={() => setModalNova(true)}><OrcamentoIcon name="plus" fallback="+" /> Nova Encomenda</button>
                </div>
            </div>

            <div className="orc-table-wrap">
                {loading ? <div className="orc-loading">A carregar...</div> : (
                    <table className="orc-table standard-table">
                        <thead>
                            <tr>
                                {(() => {
                                    // Os mesmos adereços em todas: escritos uma vez.
                                    const cf = { colFilters, setColFilters, sortCol, setSortCol, sortDir, setSortDir };
                                    return (
                                        <>
                                            <th><ColFilterEF label="Nº EF" campo="numero" {...cf} /></th>
                                            <th><ColFilterEF label="Orçamento" campo="orcamento" {...cf} /></th>
                                            <th style={{ width: 120 }}><ColFilterEF label="Fornecedor" campo="fornecedor" {...cf} /></th>
                                            <th style={{ width: 340 }}><ColFilterEF label="Descrição" campo="descricao" {...cf} /></th>
                                            <th className="orc-num"><ColFilterEF label="Qtd" campo="qtd" alinha="flex-end" {...cf} /></th>
                                            <th className="orc-num"><ColFilterEF label="V. Unit." campo="valor_unitario" alinha="flex-end" {...cf} /></th>
                                            <th className="orc-num"><ColFilterEF label="Total" campo="valor_total" alinha="flex-end" {...cf} /></th>
                                            <th><ColFilterEF label="Estado" campo="estado" {...cf} /></th>
                                            {/* A Fatura não leva filtro: é uma lista de
                                                documentos por linha, não um valor único. */}
                                            <th>Fatura</th>
                                            <th><ColFilterEF label="Data" campo="data" {...cf} /></th>
                                            <th></th>
                                        </>
                                    );
                                })()}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan={11} className="orc-empty">Nenhuma nota de encomenda encontrada</td></tr>
                            ) : filtered.map(ef => (
                                <tr key={ef.id} className="orc-row" onClick={() => setModalEf(ef)} style={{ cursor: 'pointer' }}>
                                    <td><span className="orc-id">{ef.numero}</span></td>
                                    <td onClick={e => e.stopPropagation()}>
                                        {ef.orcamento_id && (
                                            <button className="btn-ver-orc" onClick={() => navigate(`/orcamentos/${ef.orcamento_id}`)}>
                                                ORC-{ef.orcamento_id}
                                            </button>
                                        )}
                                    </td>
                                    <td style={{ maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={ef.fornecedor}>{ef.fornecedor}</td>
                                    <td style={{ maxWidth: 340, width: 340 }} onClick={e => { e.stopPropagation(); setExpandedEfRows(prev => { const s = new Set(prev); s.has(ef.id) ? s.delete(ef.id) : s.add(ef.id); return s; }); }}>
                                        <span style={{ display: 'block', whiteSpace: expandedEfRows.has(ef.id) ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: expandedEfRows.has(ef.id) ? 'clip' : 'ellipsis', cursor: 'pointer' }} title={ef.descricao}>{ef.descricao}</span>
                                    </td>
                                    <td className="orc-num">{parseFloat(ef.qtd || 0).toLocaleString('pt-PT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                    <td className="orc-num">{parseFloat(ef.valor_unitario || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} €</td>
                                    <td className="orc-num">{parseFloat(ef.valor_total || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })} €</td>
                                    <td>
                                        <span className={`ef-estado ${ef.tem_fatura_ligada ? 'ef-estado-associada' : 'ef-estado-aguarda'}`}>
                                            {ef.tem_fatura_ligada ? 'Associada' : 'Aguarda Fatura'}
                                        </span>
                                    </td>
                                    <td>{renderFaturasEF(ef)}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{ef.criado_em ? new Date(ef.criado_em).toLocaleDateString('pt-PT') : ''}</td>
                                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                                        <button className="btn-edit-ef" onClick={async () => {
                                            try {
                                                const token = localStorage.getItem('token');
                                                const res = await fetch(`${api.defaults.baseURL}/encomendas-fornecedor/${ef.id}/documento?formato=pdf`, { headers: { 'Authorization': `Bearer ${token}` } });
                                                if (!res.ok) throw new Error();
                                                const a = document.createElement('a'); a.href = URL.createObjectURL(await res.blob()); a.download = `encomenda_${ef.numero}.pdf`; a.click();
                                            } catch { notify('Erro ao gerar PDF'); }
                                        }} title="Descarregar PDF" style={{ marginRight: 4 }}><OrcamentoIcon name="document" fallback="📄" /></button>
                                        <button className="btn-edit-ef" onClick={() => setModalEf(ef)} title="Editar"><OrcamentoIcon name="edit" fallback="✏️" /></button>
                                        <button className="btn-del-orc orc-delete-icon" onClick={() => handleApagarEncomenda(ef)}
                                            disabled={deletingId !== null || ef.tem_fatura_ligada}
                                            aria-label={`Apagar encomenda ${ef.numero}`} aria-busy={deletingId === ef.id}
                                            title={ef.tem_fatura_ligada ? 'Tem fatura associada — desassocie primeiro' : 'Apagar encomenda'}>
                                            <OrcamentoIcon name="trash" fallback="🗑" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {totalPages > 1 && (
                <div className="gf-pagination" style={{ marginTop: 12 }}>
                    <button className="gf-pag-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                    <span className="gf-pag-info">Pág. {page} / {totalPages}</span>
                    <button className="gf-pag-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                </div>
            )}

            {modalEf && (
                <ModalEditEF
                    ef={modalEf}
                    onClose={() => setModalEf(null)}
                    onSaved={fetchRows}
                />
            )}

            {modalNova && (
                <ModalNovaEF
                    onClose={() => setModalNova(false)}
                    onSaved={fetchRows}
                />
            )}

            {modalImport && (
                <div style={{ position:'fixed',top:0,left:0,width:'100%',height:'100%',background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }} onClick={() => setModalImport(false)}>
                    <div style={{ background:'#fff',borderRadius:12,width:'100%',maxWidth:520,boxShadow:'0 8px 32px rgba(0,0,0,0.18)',overflow:'hidden' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'20px 24px 16px',borderBottom:'1px solid #e2e8f0' }}>
                            <h3 style={{ margin:0,fontSize:17,fontWeight:700,color:'#1e293b' }}>Importar Encomendas do Excel</h3>
                            <button onClick={() => setModalImport(false)} style={{ background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#94a3b8',lineHeight:1 }}><OrcamentoIcon name="close" fallback="✕" /></button>
                        </div>
                        <div style={{ padding:'20px 24px' }}>
                            <p style={{ fontSize:13,color:'#475569',marginBottom:16,lineHeight:1.6 }}>
                                O ficheiro deve ter as colunas: <strong>ID Trabalho, ID Encomenda, Data, Fornecedor, Pessoa Contacto, Descrição Produto, Quantidade, Valor, Factura Fornecedor, Data Factura Fornecedor</strong>.<br/>
                                O número EF ficará como <strong>EF-2025-{'{ID Encomenda}'}</strong>.
                            </p>
                            <div style={{ marginBottom:16 }}>
                                <label style={{ display:'block',fontSize:12,fontWeight:600,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6 }}>Ficheiro Excel (.xlsx, .xls)</label>
                                <input type="file" accept=".xlsx,.xls,.csv" onChange={e => { setImportFile(e.target.files[0]); setImportResult(null); setImportError(''); }} style={{ fontSize:13 }} />
                            </div>
                            {importError && <div style={{ color:'#ef4444',fontSize:13,marginBottom:12,padding:'8px 12px',background:'#fef2f2',borderRadius:6 }}>Erro: {importError}</div>}
                            {importResult && (
                                <div style={{ background:'#f0fdf4',border:'1px solid #86efac',borderRadius:8,padding:'12px 16px',marginBottom:12 }}>
                                    <div style={{ color:'#166534',fontWeight:700,marginBottom:6 }}><OrcamentoIcon name="check" fallback="✓" /> {importResult.importados} encomendas importadas com sucesso</div>
                                    {importResult._debug && (
                                        <div style={{ fontSize:11,color:'#475569',background:'#f1f5f9',borderRadius:4,padding:'6px 8px',marginBottom:8,fontFamily:'monospace' }}>
                                            <div>Colunas: fatura=<b>{importResult._debug.colunas?.colFatura||'—'}</b> | data_fatura=<b>{importResult._debug.colunas?.colDataFatura||'—'}</b> | data=<b>{importResult._debug.colunas?.colData||'—'}</b></div>
                                            {importResult._debug.amostras_com_fatura?.length > 0
                                                ? <div>Amostras: {importResult._debug.amostras_com_fatura.map(a => `${a.fatNum} / ${a.dataFatura}`).join(' | ')}</div>
                                                : <div style={{color:'#ef4444'}}><LiquidText>⚠ Nenhuma linha com nº fatura detectada</LiquidText></div>
                                            }
                                        </div>
                                    )}
                                    {importResult.fornecedores_sem_match?.length > 0 ? (
                                        <div style={{ marginBottom:10 }}>
                                            <div style={{ fontSize:12,color:'#b45309',fontWeight:700,marginBottom:6,display:'flex',alignItems:'center',gap:6 }}><LiquidText>
                                                ⚠ </LiquidText>{importResult.fornecedores_sem_match.length} fornecedor(es) sem correspondência — selecione o equivalente na BD:
                                            </div>
                                            {importResult.fornecedores_sem_match.map((nomeExcel, i) => (
                                                <div key={i} style={{ display:'flex',alignItems:'center',gap:8,marginBottom:6,padding:'6px 8px',background:'#fefce8',border:'1px solid #fde68a',borderRadius:6,flexWrap:'wrap' }}>
                                                    <span style={{ fontSize:12,color:'#92400e',fontWeight:600,minWidth:120,flex:'1 1 120px' }}>{nomeExcel}</span>
                                                    <select
                                                        value={matchSelections[nomeExcel] || ''}
                                                        onChange={e => setMatchSelections(prev => ({ ...prev, [nomeExcel]: e.target.value }))}
                                                        style={{ fontSize:12,padding:'3px 6px',border:'1px solid #d1d5db',borderRadius:4,flex:'2 1 160px',maxWidth:240 }}
                                                    >
                                                        <option value="">— Selecionar fornecedor —</option>
                                                        {fornecedoresBD.map(f => (
                                                            <option key={f.id} value={f.id}>{f.nome}</option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        onClick={() => handleMatchAplicar(nomeExcel)}
                                                        disabled={!matchSelections[nomeExcel] || matchApplying[nomeExcel]}
                                                        style={{ fontSize:12,padding:'3px 10px',border:'none',borderRadius:4,background: matchSelections[nomeExcel] && !matchApplying[nomeExcel] ? '#16a34a' : '#94a3b8',color:'#fff',cursor: matchSelections[nomeExcel] && !matchApplying[nomeExcel] ? 'pointer' : 'not-allowed',fontWeight:600,whiteSpace:'nowrap' }}
                                                    >
                                                        {matchApplying[nomeExcel] ? '...' : 'Aplicar'}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize:12,color:'#166534',marginBottom:8 }}>Todos os fornecedores foram cruzados com a base de dados.</div>
                                    )}
                                    <button onClick={handleDesfazer} style={{ background:'none',border:'1px solid #ef4444',color:'#ef4444',borderRadius:6,padding:'4px 12px',cursor:'pointer',fontSize:12,fontWeight:600 }}><LiquidText>
                                        ↩ Desfazer importação
                                    </LiquidText></button>
                                </div>
                            )}
                            <div data-liquid-actions="true" style={{ display:'flex',gap:10,justifyContent:'flex-end',marginTop:16,paddingTop:16,borderTop:'1px solid #e2e8f0' }}>
                                <button onClick={() => setModalImport(false)} style={{ padding:'8px 18px',border:'1px solid #e2e8f0',borderRadius:8,background:'#f8fafc',cursor:'pointer',fontSize:13,fontWeight:600,color:'#475569' }}>Fechar</button>
                                <button onClick={handleImport} disabled={!importFile || importLoading} style={{ padding:'8px 20px',border:'none',borderRadius:8,background: !importFile || importLoading ? '#94a3b8' : '#3b82f6',color:'#fff',cursor: !importFile || importLoading ? 'not-allowed' : 'pointer',fontSize:13,fontWeight:600 }}>
                                    {importLoading ? 'A importar...' : 'Importar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/* ============================================================
   TAB: CONFIGURAÇÕES (contratos e gestores)
   ============================================================ */
function TabConfigSection({ tipo, label }) {
    const { confirm, notify } = useOrcamentoFeedback();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [novoNome, setNovoNome] = useState('');
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editNome, setEditNome] = useState('');
    const [err, setErr] = useState('');

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/orcamentos-config', { params: {} });
            setItems((r.data || []).filter(i => i.tipo === tipo));
        } catch (e) {} finally { setLoading(false); }
    }, [tipo]);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const handleAdd = async () => {
        if (!novoNome.trim()) return;
        setSaving(true); setErr('');
        try {
            await api.post('/orcamentos-config', { tipo, nome: novoNome.trim() });
            setNovoNome('');
            fetchItems();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao adicionar');
        } finally { setSaving(false); }
    };

    const handleEdit = async (id) => {
        if (!editNome.trim()) return;
        try {
            await api.put(`/orcamentos-config/${id}`, { nome: editNome.trim() });
            setEditId(null);
            fetchItems();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao editar'); }
    };

    const handleDelete = async (id) => {
        if (!await confirm('Eliminar este item?')) return;
        try { await api.delete(`/orcamentos-config/${id}`); fetchItems(); }
        catch (e) { notify(e.response?.data?.error || 'Erro'); }
    };

    return (
        <div className="cfg-section">
            <div className="cfg-section-title"><LiquidText>{label}</LiquidText></div>
            <div className="cfg-add-row">
                <input
                    className="cfg-input"
                    placeholder={`Novo ${label.toLowerCase()}...`}
                    value={novoNome}
                    onChange={e => setNovoNome(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                />
                <button className="cfg-btn-add" onClick={handleAdd} disabled={saving || !novoNome.trim()}>
                    <OrcamentoIcon name="plus" fallback="+" /> Adicionar
                </button>
            </div>
            {err && <div className="cfg-err">{err}</div>}
            {loading ? <div className="orc-loading">A carregar...</div> : (
                <table className="orc-table cfg-table">
                    <thead>
                        <tr><th>Nome</th><th style={{ width: 120 }}></th></tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={2} className="orc-empty">Nenhum item configurado</td></tr>
                        ) : items.map(item => (
                            <tr key={item.id}>
                                <td>
                                    {editId === item.id ? (
                                        <input
                                            className="cfg-input-inline"
                                            value={editNome}
                                            onChange={e => setEditNome(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleEdit(item.id); if (e.key === 'Escape') setEditId(null); }}
                                            autoFocus
                                        />
                                    ) : item.nome}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    {editId === item.id ? (
                                        <>
                                            <button className="cfg-btn-save" aria-label="Guardar" onClick={() => handleEdit(item.id)}><OrcamentoIcon name="check" fallback="✓" /></button>
                                            <button className="cfg-btn-cancel" aria-label="Cancelar" onClick={() => setEditId(null)}><OrcamentoIcon name="close" fallback="✕" /></button>
                                        </>
                                    ) : (
                                        <>
                                            <button className="cfg-btn-edit" aria-label="Editar" onClick={() => { setEditId(item.id); setEditNome(item.nome); }}><OrcamentoIcon name="edit" fallback="✏️" /></button>
                                            <button className="cfg-btn-del" aria-label="Eliminar" onClick={() => handleDelete(item.id)}><OrcamentoIcon name="trash" fallback="✕" /></button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function ImportExcel() {
    const { confirm } = useOrcamentoFeedback();
    const fileRef = useRef(null);
    const [importing, setImporting] = useState(false);
    const [reverting, setReverting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [ultimaImport, setUltimaImport] = useState(null);

    const fetchUltima = useCallback(async () => {
        try {
            const r = await api.get('/orcamentos/importar/ultima');
            setUltimaImport(r.data);
        } catch (e) {}
    }, []);

    useEffect(() => { fetchUltima(); }, [fetchUltima]);

    const handleImport = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) { setError('Selecione um ficheiro Excel (.xls ou .xlsx)'); return; }
        setImporting(true); setError(''); setResult(null);
        try {
            const formData = new FormData();
            formData.append('ficheiro', file);
            const r = await api.post('/orcamentos/importar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 300000,
            });
            setResult(r.data);
            fileRef.current.value = '';
            fetchUltima();
        } catch (e) {
            setError(e.response?.data?.error || 'Erro ao importar');
        } finally { setImporting(false); }
    };

    const handleRevert = async () => {
        if (!await confirm(
            `Reverter última importação?\nSerão eliminados ${ultimaImport.total_importados} orçamentos` +
            (ultimaImport.gestores_criados?.length ? ` e ${ultimaImport.gestores_criados.length} gestor(es)` : '') +
            '. Esta ação não pode ser desfeita.'
        )) return;
        setReverting(true); setError('');
        try {
            const r = await api.delete('/orcamentos/importar/reverter');
            setResult({ revertido: true, message: r.data.message, gestores_removidos: r.data.gestores_removidos });
            setUltimaImport(null);
            fetchUltima();
        } catch (e) {
            setError(e.response?.data?.error || 'Erro ao reverter');
        } finally { setReverting(false); }
    };

    const fmtDataImport = (d) => d ? new Date(d).toLocaleString('pt-PT') : '';

    return (
        <div className="import-section">
            <h3>Importar Orçamentos (Excel/Access)</h3>
            <p className="import-desc">
                Importe orçamentos a partir de um ficheiro Excel (.xls/.xlsx) exportado do Access.
                O ficheiro deve ter as colunas: ID Trabalho, Data, Cliente, Projecto, Estado, etc.
                Orçamentos já importados (mesmo ID Trabalho) serão ignorados.
                Gestores de projeto são criados automaticamente se não existirem.
            </p>
            <div className="import-controls">
                <input type="file" ref={fileRef} accept=".xls,.xlsx,.csv"
                    className="import-file-input" />
                <button className="btn-primary" onClick={handleImport} disabled={importing}>
                    {importing ? 'A importar...' : 'Importar'}
                </button>
                <button className="btn-danger" onClick={handleRevert}
                    disabled={reverting || !ultimaImport}
                    title={!ultimaImport ? 'Nenhuma importação registada' : `Reverter: ${ultimaImport.ficheiro} (${ultimaImport.total_importados} orçamentos)`}>
                    {reverting ? 'A reverter...' : 'Reverter última importação'}
                </button>
            </div>

            {/* Info da última importação */}
            {ultimaImport && (
                <div className="import-ultima">
                    <div className="import-ultima-info">
                        <strong>Última importação:</strong> {fmtDataImport(ultimaImport.data_importacao)}
                        {' — '}<em>{ultimaImport.ficheiro}</em>
                        {' — '}{ultimaImport.total_importados} orçamento(s)
                        {ultimaImport.gestores_criados?.length > 0 && (
                            <span> — Gestores criados: {ultimaImport.gestores_criados.join(', ')}</span>
                        )}
                    </div>
                </div>
            )}

            {error && <div className="import-error">{error}</div>}
            {result && !result.revertido && (
                <div className="import-result">
                    <div className="import-result-summary">
                        <span className="import-ok">{result.importados} importados</span>
                        <span className="import-total">de {result.total} linhas</span>
                        {result.placeholders > 0 && (
                            <span className="import-placeholders">{result.placeholders} placeholders criados</span>
                        )}
                        {result.erros?.length > 0 && (
                            <span className="import-warn">{result.erros.length} ignorados/erros</span>
                        )}
                    </div>
                    {result.gestores_criados?.length > 0 && (
                        <div className="import-gestores-novos">
                            Gestores criados: {result.gestores_criados.join(', ')}
                        </div>
                    )}
                    {result.estados_novos?.length > 0 && (
                        <div className="import-estados-novos">
                            Estados novos encontrados: {result.estados_novos.join(', ')}
                        </div>
                    )}
                    {result.erros?.length > 0 && (
                        <div className="import-erros-list">
                            <strong>Detalhes:</strong>
                            <ul>
                                {result.erros.map((e, i) => (
                                    <li key={i}>Linha {e.linha}: {e.msg}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
            {result?.revertido && (
                <div className="import-result import-revert-ok">
                    <span className="import-ok">{result.message}</span>
                    {result.gestores_removidos?.length > 0 && (
                        <div>Gestores removidos: {result.gestores_removidos.join(', ')}</div>
                    )}
                </div>
            )}
        </div>
    );
}

const ESTADO_BADGE_MAP = {
    EM_ORCAMENTO: 'estado-orcamento', EM_PRODUCAO: 'estado-producao',
    ENVIADO: 'estado-enviado', PARA_FATURAR: 'estado-faturar', CONCLUIDO: 'estado-concluido',
    FATURADO: 'estado-facturado', FACTURADO: 'estado-facturado', CANCELADO: 'estado-cancelado', PENDENTE: 'estado-pendente',
};

function TabConfigEstados() {
    const { confirm, notify } = useOrcamentoFeedback();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [novoNome, setNovoNome] = useState('');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/orcamentos-config');
            setItems((r.data || []).filter(i => i.tipo === 'estado'));
        } catch (e) {} finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const handleAdd = async () => {
        const nome = novoNome.trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
        if (!nome) return;
        setSaving(true); setErr('');
        try {
            await api.post('/orcamentos-config', { tipo: 'estado', nome });
            setNovoNome('');
            fetchItems();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao adicionar');
        } finally { setSaving(false); }
    };

    const handleToggle = async (item) => {
        try {
            await api.put(`/orcamentos-config/${item.id}`, { ativo: !item.ativo });
            fetchItems();
        } catch (e) { notify(e.response?.data?.error || 'Erro'); }
    };

    const handleDelete = async (item) => {
        if (!await confirm(`Eliminar estado "${item.nome}"?`)) return;
        try { await api.delete(`/orcamentos-config/${item.id}`); fetchItems(); }
        catch (e) { notify(e.response?.data?.error || 'Erro'); }
    };

    return (
        <div className="cfg-section">
            <div className="cfg-section-title">Estados de Orçamento</div>
            <div className="cfg-add-row">
                <input
                    className="cfg-input"
                    placeholder="Novo estado (ex: EM_REVISAO)..."
                    value={novoNome}
                    onChange={e => setNovoNome(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                />
                <button className="cfg-btn-add" onClick={handleAdd} disabled={saving || !novoNome.trim()}>
                    <OrcamentoIcon name="plus" fallback="+" /> Adicionar
                </button>
            </div>
            {err && <div className="cfg-err">{err}</div>}
            {loading ? <div className="orc-loading">A carregar...</div> : (
                <table className="orc-table cfg-table">
                    <thead>
                        <tr><th>Estado</th><th style={{ width: 80, textAlign: 'center' }}>Activo</th><th style={{ width: 40 }}></th></tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={3} className="orc-empty">Nenhum estado configurado</td></tr>
                        ) : items.map(item => (
                            <tr key={item.id}>
                                <td>
                                    <span className={`estado-badge ${ESTADO_BADGE_MAP[item.nome] || 'estado-concluido'}`}>
                                        {ESTADO_LABELS[item.nome] || item.nome.replace(/_/g, ' ')}
                                    </span>
                                    <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 8 }}>{item.nome}</span>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        <input
                                            type="checkbox"
                                            checked={item.ativo !== false}
                                            aria-label={`Estado ${ESTADO_LABELS[item.nome] || item.nome.replace(/_/g, ' ')} ativo`}
                                            onChange={() => handleToggle(item)}
                                        />

                                    </label>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    <button className="cfg-btn-del" aria-label="Eliminar" onClick={() => handleDelete(item)}><OrcamentoIcon name="trash" fallback="✕" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function TabConfiguracoes() {
    return (
        <div className="cfg-page">
            <TabConfigSection tipo="contrato" label="Contratos" />
            <TabConfigSection tipo="gestor" label="Gestores de Projeto" />
            <TabConfigEstados />
            <ImportExcel />
        </div>
    );
}

/* ============================================================
   PÁGINA PRINCIPAL
   ============================================================ */
/* ============================================================
   TAB: ANÁLISE COMERCIAL
   ============================================================ */
const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const ANO_CORES = ['#4f46e5','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'];

const fmtEuro = (v) => {
    if (v >= 1000000) return (v/1000000).toFixed(1) + 'M €';
    if (v >= 1000) return (v/1000).toFixed(1) + 'k €';
    return parseFloat(v||0).toFixed(0) + ' €';
};

const TooltipEuro = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="analise-tooltip">
            <div className="analise-tooltip-label"><LiquidText>{label}</LiquidText></div>
            {payload.map((p, i) => (
                <div key={i} style={{ color: p.color }}>
                    {p.name}: {typeof p.value === 'number' && p.value > 100 ? fmtEuro(p.value) : p.value}
                </div>
            ))}
        </div>
    );
};

function TabAnalise() {
    const { theme } = useMaquetesTheme();
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [anosAtivos, setAnosAtivos] = useState([]);
    const anoCorrente = new Date().getFullYear();

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get('/orcamentos/analise');
                setDados(r.data);
                // Por defeito mostrar os 2 anos mais recentes
                const anos = [...new Set(r.data.por_ano.map(d => d.ano))].sort((a,b) => b-a);
                setAnosAtivos(anos.slice(0, 2));
            } catch (e) {
                setErro(e?.response?.data?.error || e?.message || 'Erro desconhecido');
            }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <div className="orc-loading">A carregar análise...</div>;
    if (erro) return <div className="orc-empty" style={{ color: '#dc2626' }}>Erro: {erro}</div>;
    if (!dados || dados.por_mes.length === 0) return <div className="orc-empty">Sem dados — os orçamentos precisam de ter data preenchida.</div>;

    const todosAnos = [...new Set(dados.por_ano.map(d => d.ano))].sort((a,b) => b-a);

    const toggleAno = (ano) => {
        setAnosAtivos(prev =>
            prev.includes(ano) ? prev.filter(a => a !== ano) : [...prev, ano]
        );
    };

    // --- Dados por mês homólogo (só anos ativos) ---
    const dadosMensais = MESES.map((mes, idx) => {
        const entry = { mes };
        anosAtivos.forEach(ano => {
            const row = dados.por_mes.find(d => d.ano === ano && d.mes === idx + 1);
            entry[`orc_${ano}`]     = row ? parseInt(row.total) : 0;
            entry[`fat_${ano}`]     = row ? parseInt(row.faturados) : 0;
            entry[`venda_${ano}`]   = row ? parseFloat(row.total_venda) : 0;
            entry[`margem_${ano}`]  = row ? parseFloat(row.total_venda) - parseFloat(row.total_compra) : 0;
        });
        return entry;
    });

    // --- Dados anuais para o gráfico de barras totais ---
    const dadosAnuais = dados.por_ano.map(d => ({
        ano: String(d.ano),
        'Orçamentos': parseInt(d.total),
        'Faturados': parseInt(d.faturados),
        'Venda': parseFloat(d.total_venda),
        'Margem': parseFloat(d.total_venda) - parseFloat(d.total_compra),
    }));

    return (
        <div className="analise-page">
            {/* Seletor de anos para comparação */}
            <div className="analise-anos-selector">
                <span className="analise-selector-label">Comparar anos:</span>
                {todosAnos.map((ano, i) => (
                    <button
                        key={ano}
                        className={`analise-ano-btn${anosAtivos.includes(ano) ? ' active' : ''}`}
                        aria-pressed={anosAtivos.includes(ano)}
                        style={theme === 'liquido' ? { '--year-accent': ANO_CORES[i % ANO_CORES.length] } : anosAtivos.includes(ano) ? { background: ANO_CORES[i % ANO_CORES.length], borderColor: ANO_CORES[i % ANO_CORES.length] } : {}}
                        onClick={() => toggleAno(ano)}
                    >
                        {ano === anoCorrente ? `${ano} (atual)` : ano}
                    </button>
                ))}
            </div>

            {/* Grid de KPIs anuais */}
            <div className="analise-kpis">
                {[...anosAtivos].sort((a,b) => b-a).map((ano, i) => {
                    const row = dados.por_ano.find(d => d.ano === ano);
                    if (!row) return null;
                    const margem = parseFloat(row.total_venda) - parseFloat(row.total_compra);
                    const pct = row.total_venda > 0 ? (margem / row.total_venda * 100).toFixed(1) : 0;
                    return (
                        <div key={ano} className="analise-kpi-card" style={{ borderTopColor: ANO_CORES[todosAnos.indexOf(ano) % ANO_CORES.length] }}>
                            <div className="analise-kpi-ano">{ano}</div>
                            <div className="analise-kpi-row">
                                <div className="analise-kpi-item">
                                    <span className="analise-kpi-val">{row.total}</span>
                                    <span className="analise-kpi-label">Orçamentos</span>
                                </div>
                                <div className="analise-kpi-item">
                                    <span className="analise-kpi-val">{row.faturados}</span>
                                    <span className="analise-kpi-label">Faturados</span>
                                </div>
                                <div className="analise-kpi-item">
                                    <span className="analise-kpi-val">{fmtEuro(row.total_venda)}</span>
                                    <span className="analise-kpi-label">Faturação</span>
                                </div>
                                <div className="analise-kpi-item">
                                    <span className="analise-kpi-val">{fmtEuro(margem)}</span>
                                    <span className="analise-kpi-label">Margem ({pct}%)</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Faturados e faturação contam pela data da fatura; sem data não há mês onde os pôr */}
            {dados.por_ano.filter(d => anosAtivos.includes(d.ano) && d.faturados_sem_data > 0).length > 0 && (
                <div style={{ margin: '-6px 0 14px', padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: '0.82rem', color: '#92400e' }}>
                    <OrcamentoIcon name="warning" fallback="⚠️" /> Fora dos totais por não terem data de fatura:{' '}
                    {dados.por_ano.filter(d => anosAtivos.includes(d.ano) && d.faturados_sem_data > 0)
                        .map(d => `${d.faturados_sem_data} orçamento(s) de ${d.ano} marcados como faturados`).join(' · ')}.
                    {' '}Preencha a data da fatura para entrarem no mês certo.
                </div>
            )}

            <div className="analise-charts-grid">
                {/* Gráfico 1: Nº orçamentos por mês homólogo */}
                <div className="analise-chart-card">
                    <h4>Nº Orçamentos por Mês <small style={{ fontWeight: 400, color: '#94a3b8' }}>· pela data do orçamento</small></h4>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={dadosMensais} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} width={30} />
                            <Tooltip content={<TooltipEuro />} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {[...anosAtivos].sort((a,b) => b-a).map((ano, i) => (
                                <Line key={ano} type="monotone" dataKey={`orc_${ano}`}
                                    name={String(ano)} stroke={ANO_CORES[todosAnos.indexOf(ano) % ANO_CORES.length]}
                                    strokeWidth={2} dot={{ r: 3 }} />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Gráfico 2: Nº faturados por mês homólogo */}
                <div className="analise-chart-card">
                    <h4>Nº Faturados por Mês <small style={{ fontWeight: 400, color: '#94a3b8' }}>· pela data da fatura</small></h4>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={dadosMensais} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} width={30} />
                            <Tooltip content={<TooltipEuro />} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {[...anosAtivos].sort((a,b) => b-a).map((ano, i) => (
                                <Line key={ano} type="monotone" dataKey={`fat_${ano}`}
                                    name={String(ano)} stroke={ANO_CORES[todosAnos.indexOf(ano) % ANO_CORES.length]}
                                    strokeWidth={2} dot={{ r: 3 }} />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Gráfico 3: Faturação mensal (venda) */}
                <div className="analise-chart-card">
                    <h4>Faturação por Mês (€) <small style={{ fontWeight: 400, color: '#94a3b8' }}>· pela data da fatura</small></h4>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={dadosMensais} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} width={55} tickFormatter={fmtEuro} />
                            <Tooltip content={<TooltipEuro />} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {[...anosAtivos].sort((a,b) => b-a).map((ano, i) => (
                                <Bar key={ano} dataKey={`venda_${ano}`}
                                    name={String(ano)} fill={ANO_CORES[todosAnos.indexOf(ano) % ANO_CORES.length]}
                                    radius={[3,3,0,0]} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Gráfico 4: Margem mensal */}
                <div className="analise-chart-card">
                    <h4>Margem por Mês (€) <small style={{ fontWeight: 400, color: '#94a3b8' }}>· pela data da fatura</small></h4>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={dadosMensais} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} width={55} tickFormatter={fmtEuro} />
                            <Tooltip content={<TooltipEuro />} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {[...anosAtivos].sort((a,b) => b-a).map((ano, i) => (
                                <Bar key={ano} dataKey={`margem_${ano}`}
                                    name={String(ano)} fill={ANO_CORES[todosAnos.indexOf(ano) % ANO_CORES.length]}
                                    radius={[3,3,0,0]} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Gráfico 5: Totais anuais - overview */}
                <div className="analise-chart-card analise-chart-full">
                    <h4>Resumo Anual — Orçamentos vs Faturados</h4>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={dadosAnuais} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="ano" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} width={35} />
                            <Tooltip content={<TooltipEuro />} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="Orçamentos" fill="#4f46e5" radius={[3,3,0,0]} />
                            <Bar dataKey="Faturados"  fill="#10b981" radius={[3,3,0,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Gráfico 6: Faturação + Margem anual */}
                <div className="analise-chart-card analise-chart-full">
                    <h4>Resumo Anual — Faturação e Margem (€)</h4>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={dadosAnuais} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="ano" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} width={60} tickFormatter={fmtEuro} />
                            <Tooltip content={<TooltipEuro />} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="Venda"  name="Faturação" fill="#0ea5e9" radius={[3,3,0,0]} />
                            <Bar dataKey="Margem" name="Margem"    fill="#f59e0b" radius={[3,3,0,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

const TABS = [
    { key: 'lista',        label: 'Lista de Orçamentos' },
    { key: 'analise',      label: 'Análise Comercial' },
    { key: 'encomendas',   label: 'Encomendas Fornecedor' },
    { key: 'clientes',     label: 'Clientes' },
    { key: 'fornecedores', label: 'Fornecedores' },
    { key: 'configuracoes',label: 'Configurações' },
];

function OrcamentosContent() {
    const navigate = useNavigate();
    const location = useLocation();
    const [tab, setTab] = useState(() => { try { return location.state?.tab || localStorage.getItem('orc_tab') || 'lista'; } catch { return 'lista'; } });
    const [stats, setStats] = useState({});
    const [filtroEstado, setFiltroEstado] = useState(() => { try { return localStorage.getItem('orc_filtroEstado') || ''; } catch { return ''; } });
    const anoCorrente = new Date().getFullYear();
    const [filtroAno, setFiltroAno] = useState(() => { try { return localStorage.getItem('orc_filtroAno') || String(anoCorrente); } catch { return String(anoCorrente); } });
    useEffect(() => { try { localStorage.setItem('orc_tab', tab); } catch {} }, [tab]);
    useEffect(() => { try { localStorage.setItem('orc_filtroEstado', filtroEstado); } catch {} }, [filtroEstado]);
    useEffect(() => { try { localStorage.setItem('orc_filtroAno', filtroAno); } catch {} }, [filtroAno]);
    const [anosDisponiveis, setAnosDisponiveis] = useState(_cache.anos || []);

    const fetchAnos = useCallback(async (silent = false) => {
        try {
            const r = await api.get('/orcamentos/anos');
            _cache.anos = r.data;
            setAnosDisponiveis(r.data);
        } catch (e) {}
    }, []);

    useEffect(() => { fetchAnos(); }, [fetchAnos]);

    const fetchStats = useCallback(async (silent = false) => {
        try {
            const params = {};
            if (filtroAno) params.ano = filtroAno;
            const r = await api.get('/orcamentos/stats', { params });
            _cache.stats = r.data;
            setStats(r.data);
        } catch (e) {}
    }, [filtroAno]);

    useEffect(() => {
        if (_cache.stats) {
            setStats(_cache.stats);
            fetchStats(true);
        } else {
            fetchStats(false);
        }
    }, [fetchStats]);

    const [hojeRows, setHojeRows] = useState([]);
    const [hojeGestor, setHojeGestor] = useState(() => { try { return localStorage.getItem('orc_hojeGestor') || ''; } catch { return ''; } });
    useEffect(() => { try { localStorage.setItem('orc_hojeGestor', hojeGestor); } catch {} }, [hojeGestor]);

    const fetchHoje = useCallback(async () => {
        try {
            const r = await api.get('/orcamentos', { params: { hoje: 1 } });
            setHojeRows(r.data || []);
        } catch (e) {}
    }, []);
    useEffect(() => { if (tab === 'lista') fetchHoje(); }, [tab, fetchHoje]);

    const hojeGestores = [...new Set(hojeRows.map(r => r.gestor_projeto).filter(Boolean))].sort();
    const hojeFiltered = hojeGestor ? hojeRows.filter(r => r.gestor_projeto === hojeGestor) : hojeRows;

    const PAINEIS = [
        { key: 'TODOS',        label: 'Todos',         count: stats.total        || 0, cls: 'painel-todos' },
        { key: 'EM_ORCAMENTO', label: 'Em Orçamento',  count: stats.em_orcamento || 0, cls: 'painel-orcamento' },
        { key: 'EM_PRODUCAO',  label: 'Em Produção',   count: stats.em_producao  || 0, cls: 'painel-producao' },
        { key: 'PARA_FATURAR', label: 'Para Faturar',   count: stats.para_faturar || 0, cls: 'painel-faturar' },
    ];

    return (
        <div className="orc-page">
            {/* Barra branca do topo — igual às outras páginas */}
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1><OrcamentoIcon name="briefcase" fallback="💼" /> Orçamentos</h1>
                        <p>Gestão de orçamentos, clientes e fornecedores</p>
                    </div>
                </div>
                <div className="top-bar-actions">
                    <ProcedimentoLink codigo="PR.CM.01" titulo="Procedimento Comercial" />
                    {tab === 'lista' && (
                        <button className="btn-primary" onClick={() => navigate('/orcamentos/novo')}>
                            <OrcamentoIcon name="plus" fallback="+" /> Novo Orçamento
                        </button>
                    )}
                    {tab === 'clientes' && (
                        <span className="top-bar-hint">Clique numa linha para editar</span>
                    )}
                </div>
            </div>

            {/* Conteúdo com padding */}
            <div className="orc-body">
                {/* Painéis — linha única full width */}
                {tab === 'lista' && (
                    <div className="orc-paineis">
                        {PAINEIS.map(p => {
                            const isActive = p.key === 'TODOS'
                                ? filtroEstado === '' || filtroEstado === 'TODOS'
                                : filtroEstado === p.key;
                            const handleClick = () => {
                                if (p.key === 'TODOS') setFiltroEstado('');
                                else setFiltroEstado(filtroEstado === p.key ? '' : p.key);
                            };
                            return (
                                <div key={p.key} className={`orc-painel ${p.cls}${isActive ? ' painel-active' : ''}`}
                                    onClick={handleClick}>
                                    <span className="painel-num">{p.count}</span>
                                    <span className="painel-label"><LiquidText>{p.label}</LiquidText></span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Entregas Hoje */}
                {tab === 'lista' && (
                    <div className="orc-hoje-panel">
                            <div className="orc-hoje-header">
                                <span><OrcamentoIcon name="calendar" fallback="📅" /> Entregas Hoje</span>
                                <div className="orc-hoje-header-right">
                                    {hojeGestores.length > 0 && (
                                        <select
                                            className="orc-hoje-gestor-sel"
                                            value={hojeGestor}
                                            onChange={e => setHojeGestor(e.target.value)}
                                        >
                                            <option value="">Todos os gestores</option>
                                            {hojeGestores.map(g => (
                                                <option key={g} value={g}>{g}</option>
                                            ))}
                                        </select>
                                    )}
                                    {hojeFiltered.length > 0 && (
                                        <span className="orc-hoje-count">{hojeFiltered.length}</span>
                                    )}
                                </div>
                            </div>
                            <div className="orc-hoje-list orc-hoje-3col">
                                {hojeFiltered.length === 0 ? (
                                    <div className="orc-hoje-empty">Sem entregas para hoje</div>
                                ) : hojeFiltered.map(r => (
                                    <div key={r.id} className="orc-hoje-item" onClick={() => navigate(`/orcamentos/${r.id}`)}>
                                        <span className="orc-hoje-id">{r.id}</span>
                                        <span className="orc-hoje-cliente">{r.cliente}</span>
                                        <span className="orc-hoje-sep">·</span>
                                        <span className="orc-hoje-proj">{r.projeto}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                )}

                {/* Sub-navegação */}
                <div className="shared-subnav">
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            className={`shared-subnav-btn ${tab === t.key ? 'active' : ''}`}
                            onClick={() => setTab(t.key)}
                        >
                            <LiquidText>{t.label}</LiquidText>
                        </button>
                    ))}
                </div>

                {/* Filtro ano — sempre por baixo do subnav */}
                {tab === 'lista' && anosDisponiveis.length > 0 && (
                    <div className="orc-ano-filter">
                        <button
                            className={`orc-ano-btn${filtroAno === '' ? ' active' : ''}`}
                            onClick={() => setFiltroAno('')}
                        >
                            Todos
                        </button>
                        {anosDisponiveis.map(a => (
                            <button
                                key={a}
                                className={`orc-ano-btn${filtroAno === String(a) ? ' active' : ''}`}
                                onClick={() => setFiltroAno(String(a))}
                            >
                                {a === anoCorrente ? `Ano Corrente (${a})` : a}
                            </button>
                        ))}
                    </div>
                )}

                <div className="orc-card">
                    <div className="orc-tab-content">
                        {tab === 'lista'       && <TabLista filtroEstado={filtroEstado} setFiltroEstado={setFiltroEstado} filtroAno={filtroAno} setFiltroAno={setFiltroAno} onRefreshStats={() => { fetchStats(); fetchAnos(); }} />}
                        {tab === 'analise'     && <TabAnalise />}
                        {tab === 'encomendas'  && <TabEncomendasFornecedor autoOpenNova={!!location.state?.openNova} />}
                        {tab === 'clientes'    && <TabClientes />}
                        {tab === 'fornecedores'   && <TabFornecedores />}
                        {tab === 'configuracoes'  && <TabConfiguracoes />}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Orcamentos() {
    return <OrcamentoFeedbackProvider><OrcamentosContent /></OrcamentoFeedbackProvider>;
}
